/**
 * The game itself: the rules, the loop, and everything that talks to the page.
 *
 * Derived from the Cannon showcase, which is one barrel, one ball and a wall
 * of boxes. This keeps the part that made Cannon worth looking at, a heavy
 * bullet-flagged shot into a stack that comes apart, and turns the camera
 * ninety degrees. In plan view the barrel can point anywhere, the wall can be
 * anywhere, and once the wall can be *anywhere* you have a map; once you have
 * a map you need somewhere to get to, something in the way, and a reason to
 * hurry. That is the whole design.
 *
 * Everything the visitor sees other than the canvas is DOM, rendered by the
 * page and driven from here: the screens, the HUD, the two thumb sticks. A
 * canvas cannot be read by a screen reader and a heads-up display made of
 * divs costs nothing.
 */

import { Vec2, type Body, type World } from "planck";
import { Particles } from "./particles.ts";
import { Sound } from "./audio.ts";
import { Input, type Intent } from "./input.ts";
import { MODS, Progress, readSetting, writeSetting, type ModId } from "./mods.ts";
import { TANKS, tankById, type TankId, type TankSpec } from "./tanks.ts";
import { SOUND_CREDITS } from "./credits.ts";
import {
  blast,
  bodies,
  CAT,
  colours,
  entityOf,
  feel,
  lineOfSight,
  makeBox,
  makeCircle,
  makeWorld,
  MASK,
  shove,
  sweptHit,
  type Entity,
  type Perk,
  type PowerName,
  type Team,
} from "./world.ts";
import { levels, parseLevel, roadStep, TILE, tileCentre, type Level } from "./levels.ts";
import { doorsFor, generateYard, rng, type Door, type YardKind } from "./generate.ts";
import {
  cameraScale,
  paintTankPreview,
  render,
  screenToWorld,
  worldToScreen,
  type Camera,
  type Scene,
  type View,
} from "./render.ts";

const STEP = 1 / 60;
const BEST_KEY = "paper-tank.best-wrecked";

type State = "title" | "achievements" | "credits" | "paint" | "settings" | "brief" | "playing" | "paused" | "clear" | "over" | "win";

type Weapon = {
  name: string;
  cooldown: number;
  speed: number;
  damage: number;
  radius: number;
  shots: number;
  spread: number;
  bounces: number;
  blast: number;
};

const WEAPONS: Record<PowerName, Weapon> = {
  cannon: { name: "Cannon", cooldown: 0.42, speed: 42, damage: 1, radius: 0.2, shots: 1, spread: 0, bounces: 0, blast: 0 },
  triple: { name: "Triple", cooldown: 0.54, speed: 38, damage: 1, radius: 0.17, shots: 3, spread: 0.17, bounces: 0, blast: 0 },
  bouncer: { name: "Bouncer", cooldown: 0.4, speed: 34, damage: 1, radius: 0.19, shots: 1, spread: 0, bounces: 3, blast: 0 },
  heavy: { name: "Heavy", cooldown: 0.95, speed: 26, damage: 3, radius: 0.36, shots: 1, spread: 0, bounces: 0, blast: 3.4 },
};

const PICKUP_BY_CHAR: Record<string, { power?: PowerName; perk?: Perk }> = {
  "1": { power: "triple" },
  "2": { power: "bouncer" },
  "3": { power: "heavy" },
  "+": { perk: "repair" },
  "@": { perk: "shield" },
};
/** How long a dropped repair waits, and how long before another can come. */
const DROP_FUSE = 14;
const DROP_REST = 28;

/**
 * The workshop. A tank has five numbers and each level clear earns points
 * to put on them, one at a time, five steps each. Beside that, one trade a
 * level: something for something, take it or leave it, two a run at most.
 */
type StatId = "armour" | "speed" | "damage" | "shell" | "rate";
const STATS: Array<{ id: StatId; name: string; blurb: string }> = [
  { id: "armour", name: "Armour", blurb: "+1 hit per step" },
  { id: "speed", name: "Speed", blurb: "+8% drive per step" },
  { id: "damage", name: "Damage", blurb: "+25% per step" },
  { id: "shell", name: "Shell speed", blurb: "+10% per step" },
  { id: "rate", name: "Fire rate", blurb: "8% quicker per step" },
];
const STAT_MAX = 5;
type Trade = { id: string; name: string; give: string; take: string };
/**
 * Trades come from the Endless Deal door, free, two a run at most. Each
 * gives more than two stat steps could buy, and takes something back.
 */
const TRADES: Trade[] = [
  { id: "frag", name: "Frag Shells", give: "Your shells explode where they land", take: "Your gun fires 25% slower" },
  { id: "skip", name: "Ricochet Shells", give: "Your shells bounce off walls one more time", take: "Lose 1 armour, and a bounced shell of yours can hit you" },
  { id: "glass", name: "Thin Armour", give: "Your shells hit twice as hard", take: "Lose 2 armour" },
  { id: "match", name: "Long Power-ups", give: "Power-ups last twice as long", take: "10% less drive" },
  { id: "foot", name: "Bigger Engine", give: "25% more drive, boost recharges 60% faster", take: "Your gun fires 10% slower" },
  { id: "eye", name: "Faster Shells", give: "Your shells fly 50% faster", take: "Your shells hit 20% less hard" },
];
const TRADE_MAX = 2;

/** The paint shop: colours a marker set comes in, and what to scrawl. */
export const PAINTS: Array<{ id: string; name: string; colour: string | null }> = [
  { id: "tank", name: "Factory", colour: null },
  { id: "green", name: "Green", colour: "#02602a" },
  { id: "teal", name: "Teal", colour: "#0b6f7a" },
  { id: "blue", name: "Blue", colour: "#2b4c8c" },
  { id: "purple", name: "Purple", colour: "#5b3a8a" },
  { id: "orange", name: "Orange", colour: "#d07a1a" },
  { id: "charcoal", name: "Charcoal", colour: "#3a3a34" },
];
export const DECALS: Array<{ id: string; name: string }> = [
  { id: "none", name: "Plain" },
  { id: "stripe", name: "Stripe" },
  { id: "star", name: "Star" },
  { id: "skull", name: "Skull" },
  { id: "bolt", name: "Bolt" },
  { id: "seven", name: "Seven" },
  { id: "flames", name: "Flames" },
  { id: "flower", name: "Flower" },
  { id: "camo", name: "Camo" },
  { id: "roundel", name: "Roundel" },
  { id: "chevrons", name: "Chevrons" },
  { id: "checks", name: "Checks" },
  { id: "heart", name: "Heart" },
  { id: "wave", name: "Waves" },
];
type PaintJob = { colour: string; decal: string };
const PAINT_KEY = "paint";


const POWER_SECONDS = 18;

export type RunOptions = { reduced: boolean; preview?: boolean; desktop?: boolean };

/** Something that can be torn down, and optionally replayed. */
export type Revertible = { revert(): unknown; replay?: () => void };

export function runTank(root: HTMLElement, opts: RunOptions): Revertible {
  const game = new Game(root, opts);
  return { revert: () => game.dispose() };
}

class Game {
  private canvas: HTMLCanvasElement;
  private c: CanvasRenderingContext2D;
  private dpr = 1;
  private view: View = { width: 1, height: 1 };
  private stage: HTMLElement | null;

  private world: World = makeWorld();
  private particles = new Particles();
  private sound = new Sound();
  private input: Input | null = null;

  private state: State = "title";
  private raf = 0;
  private last = 0;
  private accumulator = 0;
  private time = 0;

  private player: Body | null = null;
  private actors: Body[] = [];
  private shells: Body[] = [];
  private originals = new Set<Body>();
  private exits: Body[] = [];
  private spawners: Array<{ x: number; y: number }> = [];
  private reap = new Set<Body>();
  private contacts: Array<[Body, Body]> = [];
  private pending: Array<{ x: number; y: number; radius: number; damage: number; power: number }> = [];

  private levelIndex = 0;
  private levelTime = 0;
  private score = 0;
  private levelScore = 0;
  private tanksWrecked = 0;
  private levelTanks = 0;
  /** Hits the shield will still take, and for how long it holds. */
  private shield = 0;
  private shieldTimer = 0;
  private drop: Body | null = null;
  private dropRest = 0;
  /** This run's workshop: steps on each stat, the trades taken, points to spend. */
  private stats: Record<StatId, number> = { armour: 0, speed: 0, damage: 0, shell: 0, rate: 0 };
  private trades: string[] = [];
  private points = 0;
  private aimLocked = readSetting("aim", "mouse") === "hull";
  private shakeOn = readSetting("shake", "on") !== "off";
  private dropsLeft = 2;
  private paintJob: PaintJob = { colour: "tank", decal: "none" };
  private lastAlert = 0;
  /** Until when the whole yard is on edge, after one of them was hit. */
  private alertUntil = 0;
  private returnTo: State = "title";
  /** True while the drawn gunsight is standing in for the pointer. */
  private hidCursor = false;
  /**
   * The attract loop: behind the title sheet a tank drives itself round a
   * yard, so the front of the game is the game. Nothing it does counts:
   * it scores nothing, unlocks nothing, and makes no noise.
   */
  private attract = false;
  /** A run: generated yards, carried damage, and a door at the end of each. */
  private mode: "campaign" | "run" = "campaign";
  private current: Level = levels[0] as Level;
  private depth = 1;
  private runSeed = 0;
  private doors: Door[] = [];
  private carried = 0;
  private nextKind: YardKind = "yard";
  private grantPower: PowerName | null = null;
  private grantShield = false;
  private bonusArmour = 0;
  private best = 0;
  private combo = 0;
  private comboTimer = 0;
  private heat = 0;
  private spawnTimer = 0;
  private reinforcements = 0;
  private remaining = 0;

  /** Seconds left on each power-up. Two at once is the point. */
  private powers: Partial<Record<PowerName, number>> = {};
  private ricochets = 0;
  private progress = new Progress();
  /**
   * North stays up. The machinery for turning the world with the hull is
   * still below, unused, in case that view is ever wanted back.
   */
  private headingCam = false;
  private tankId: TankId = "standard";
  private cooldown = 0;
  private boostTimer = 0;
  private boostCharge = 1;
  private invuln = 0;
  private intent: Intent | null = null;

  private cam: Camera = { x: 0, y: 0, scale: 40, rot: 0, shakeX: 0, shakeY: 0 };
  private shake = 0;
  private hitStop = 0;
  private full = false;
  private observer: ResizeObserver | null = null;
  private offs: Array<() => void> = [];
  private ui: Record<string, HTMLElement | null> = {};
  private mapSize = { w: 10, h: 10 };
  /** Road tiles by "col,row", and the list the paper draws. */
  private roadSet = new Set<string>();
  private roads: Array<{ x: number; y: number; e: boolean; w: boolean; n: boolean; s: boolean }> = [];
  private cars: Body[] = [];
  private factories: Body[] = [];

  constructor(
    private root: HTMLElement,
    private opts: RunOptions,
  ) {
    this.stage = root.closest<HTMLElement>("[data-stage]");
    this.canvas = document.createElement("canvas");
    this.canvas.className = "tank__canvas";
    root.prepend(this.canvas);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("No 2D context for the tank showcase");
    this.c = ctx;

    this.measure();
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(root);

    if (!opts.preview) {
      this.collectUi();
      this.input = new Input(root, {
        onPause: () => this.togglePause(),
        onTouch: () => root.setAttribute("data-touch", ""),
      });
      this.best = readBest();
      this.setText("bestTitle", this.best ? String(this.best) : "none");
      this.bindButtons();
      this.tankId = this.pickTank(readSetting("tank", "standard"));
      this.paintJob = readPaint();
      this.progress.newTanks(TANKS, true);
      this.renderPicker();
      this.renderAchievements();
      this.renderCredits();
      this.renderPaint();
      this.syncLabels();
      this.on(document, "visibilitychange", () => {
        if (document.hidden && this.state === "playing") this.togglePause();
      });
    }

    this.loadLevel(opts.preview ? 2 : 0);
    this.setState(opts.preview ? "playing" : "title");

    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  // ---- plumbing ------------------------------------------------------------

  private on(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler);
    this.offs.push(() => target.removeEventListener(type, handler));
  }

  private measure(): void {
    const rect = this.root.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    if (width === this.view.width && height === this.view.height) return;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.view = { width, height };
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.cam.scale = this.opts.preview
      ? Math.min(width, height) / 15
      : cameraScale(this.view);
  }

  private collectUi(): void {
    const pick = (name: string) => this.root.querySelector<HTMLElement>(`[data-ui="${name}"]`);
    for (const name of [
      "title", "brief", "pause", "clear", "over", "win", "hud",
      "hp", "weapon", "power", "score", "level", "shield", "time",
      "briefNumber", "briefName", "briefObjective",
      "clearScore", "clearTime", "clearTitle",
      "finalScore", "bestFinal", "winScore", "bestWin", "bestTitle",
      "heat", "boost",
      "achievements", "achievementList", "achievementCount", "toast",
      "tankStrip", "clearTanks", "credits", "creditsList",
      "paint", "paintColours", "paintDecals", "paintPreview", "workshop", "workshopPicks", "workshopList",
      "settings", "aimHint", "musicVolume", "musicValue", "sfxVolume", "sfxValue",
      "doors", "next", "overTitle",
    ]) {
      this.ui[name] = pick(name);
    }
  }

  private setText(name: string, value: string): void {
    const el = this.ui[name];
    if (el && el.textContent !== value) el.textContent = value;
  }

  private bindButtons(): void {
    const act = (name: string, fn: () => void) => {
      this.root.querySelectorAll<HTMLElement>(`[data-act="${name}"]`).forEach((el) => {
        const handler = (event: Event) => {
          event.preventDefault();
          this.sound.play("select");
          fn();
        };
        el.addEventListener("click", handler);
        this.offs.push(() => el.removeEventListener("click", handler));
      });
    };

    act("play", () => {
      this.sound.unlock();
      this.mode = "campaign";
      this.score = 0;
      this.tanksWrecked = 0;
      this.resetWorkshop();
      this.loadLevel(0);
      void this.enterFullscreen();
      this.brief();
    });
    act("resume", () => this.togglePause());
    // "Endless" on the title sheet. A run is still what one attempt is
    // called, here and in the copy, so the mode keeps that name.
    act("run", () => {
      this.sound.unlock();
      this.mode = "run";
      this.depth = 1;
      this.runSeed = Math.floor(Math.random() * 0xffffff);
      this.score = 0;
      this.tanksWrecked = 0;
      this.resetWorkshop();
      this.nextKind = "yard";
      this.loadYard("yard");
      this.brief();
      void this.enterFullscreen();
    });
    act("achievements", () => this.setState("achievements"));
    act("paint", () => this.setState("paint"));
    const paint = this.ui.paint;
    if (paint) {
      const handler = (event: Event) => {
        const button = (event.target as Element | null)?.closest<HTMLElement>("[data-paint], [data-decal]");
        if (!button) return;
        if (button.dataset.paint) this.paintJob.colour = button.dataset.paint;
        if (button.dataset.decal) this.paintJob.decal = button.dataset.decal;
        writeSetting(PAINT_KEY, JSON.stringify(this.paintJob));
        this.sound.unlock();
        this.sound.play(button.dataset.paint ? "spray" : "marker");
        this.renderPaint();
        this.renderPicker();
      };
      paint.addEventListener("click", handler);
      this.offs.push(() => paint.removeEventListener("click", handler));
    }
    const doors = this.ui.doors;
    if (doors) {
      const handler = (event: Event) => {
        const button = (event.target as Element | null)?.closest<HTMLElement>("[data-door]");
        if (!button) return;
        const door = this.doors[Number(button.dataset.door)];
        if (!door) return;
        this.takeDoor(door);
        this.sound.play("open", 0.8);
        this.loadYard(this.nextKind);
        this.brief();
      };
      doors.addEventListener("click", handler);
      this.offs.push(() => doors.removeEventListener("click", handler));
    }

    const shop = this.ui.workshop;
    if (shop) {
      const handler = (event: Event) => {
        const button = (event.target as Element | null)?.closest<HTMLElement>("[data-stat]");
        if (!button) return;
        const id = button.dataset.stat as StatId;
        if (this.points <= 0 || this.stats[id] >= STAT_MAX) return;
        this.stats[id] += 1;
        this.points -= 1;
        this.sound.play("stack", 0.7);
        this.renderWorkshop();
      };
      shop.addEventListener("click", handler);
      this.offs.push(() => shop.removeEventListener("click", handler));
    }
    act("settings", () => {
      this.returnTo = this.state === "paused" ? "paused" : "title";
      this.setState("settings");
    });
    act("back", () => this.setState(this.returnTo));
    const setAim = (locked: boolean) => {
      this.aimLocked = locked;
      writeSetting("aim", locked ? "hull" : "mouse");
      this.syncLabels();
    };
    act("aim-mouse", () => setAim(false));
    act("aim-locked", () => setAim(true));
    this.bindVolume("musicVolume", (v) => {
      this.sound.setMusicVolume(v);
      this.sound.music(this.moodFor(this.state));
    });
    this.bindVolume("sfxVolume", (v) => {
      this.sound.setSfxVolume(v);
      this.sound.play("pickup", 0.5);
    });
    act("shake", () => {
      this.shakeOn = !this.shakeOn;
      writeSetting("shake", this.shakeOn ? "on" : "off");
      this.syncLabels();
    });
    act("credits", () => this.setState("credits"));
    const strip = this.ui.tankStrip;
    if (strip) {
      const handler = (event: Event) => {
        const button = (event.target as Element | null)?.closest<HTMLElement>("[data-tank-id]");
        if (!button) return;
        const id = button.dataset.tankId as TankId;
        const spec = tankById(id);
        if (spec.earned(this.progress.stats)) {
          this.tankId = id;
          writeSetting("tank", id);
          this.sound.play("select");
          this.renderPaint();
        } else {
          this.toast(`${spec.name}: ${spec.unlock}`);
        }
        this.renderPicker();
      };
      strip.addEventListener("click", handler);
      this.offs.push(() => strip.removeEventListener("click", handler));
    }
    const list = this.ui.achievementList;
    if (list) {
      const handler = (event: Event) => {
        const button = (event.target as Element | null)?.closest<HTMLElement>("[data-mod]");
        if (!button) return;
        const id = button.dataset.mod as ModId;
        if (this.progress.unlocked(id)) {
          this.progress.toggle(id);
          this.sound.play("select");
        }
        this.renderAchievements();
      };
      list.addEventListener("click", handler);
      this.offs.push(() => list.removeEventListener("click", handler));
    }
    act("restart", () => {
      this.score -= this.levelScore;
      this.tanksWrecked -= this.levelTanks;
      this.loadLevel(this.levelIndex);
      this.brief();
    });
    act("next", () => {
      this.loadLevel(this.levelIndex + 1);
      this.brief();
    });
    act("retry", () => {
      if (this.mode === "run") {
        // A run does not get a second go at a yard; it gets another run.
        this.depth = 1;
        this.runSeed = Math.floor(Math.random() * 0xffffff);
        this.score = 0;
        this.tanksWrecked = 0;
        this.resetWorkshop();
        this.loadYard("yard");
        this.brief();
        return;
      }
      this.score -= this.levelScore;
      this.tanksWrecked -= this.levelTanks;
      this.loadLevel(this.levelIndex);
      this.brief();
    });
    act("again", () => {
      this.mode = "campaign";
      this.score = 0;
      this.tanksWrecked = 0;
      this.resetWorkshop();
      this.loadLevel(0);
      this.brief();
    });
    act("quit", () => {
      void this.exitFullscreen();
      this.loadLevel(0);
      this.setState("title");
    });
    act("pause", () => this.togglePause());
    act("sound", () => {
      this.sound.unlock();
      this.sound.setMuted(!this.sound.muted);
      this.reflectSound();
    });
    act("full", () => {
      if (this.full) void this.exitFullscreen();
      else void this.enterFullscreen();
    });
    // Esc leaves native full screen without asking us; keep the switch honest.
    const onFull = () => {
      if (!document.fullscreenElement && this.full && !this.stage?.classList.contains("stage--full")) {
        this.full = false;
        this.syncLabels();
      }
    };
    document.addEventListener("fullscreenchange", onFull);
    this.offs.push(() => document.removeEventListener("fullscreenchange", onFull));
    this.reflectSound();
  }

  private reflectSound(): void {
    const on = !this.sound.muted;
    this.root.querySelectorAll<HTMLElement>('[data-act="sound"]').forEach((el) => {
      el.setAttribute("aria-checked", String(on));
    });
    // With everything muted the sliders would only mislead.
    this.root.querySelectorAll<HTMLInputElement>(".slider").forEach((el) => {
      el.disabled = !on;
    });
  }

  /**
   * A volume slider. It reads the stored level on the way in, and plays
   * through `apply` as it moves (on release for the effects tick, so a drag
   * is not a machine gun of pickups).
   */
  private bindVolume(name: "musicVolume" | "sfxVolume", apply: (v: number) => void): void {
    const input = this.ui[name] as HTMLInputElement | null;
    const value = this.ui[name === "musicVolume" ? "musicValue" : "sfxValue"];
    if (!input) return;
    const show = (v: number) => {
      input.value = String(Math.round(v * 100));
      input.style.setProperty("--fill", `${Math.round(v * 100)}%`);
      if (value) value.textContent = String(Math.round(v * 100));
    };
    show(name === "musicVolume" ? this.sound.musicVolume : this.sound.sfxVolume);
    const live = () => {
      this.sound.unlock();
      const v = Number(input.value) / 100;
      show(v);
      if (name === "musicVolume") apply(v);
      else this.sound.setSfxVolume(v);
    };
    const done = () => apply(Number(input.value) / 100);
    input.addEventListener("input", live);
    input.addEventListener("change", done);
    this.offs.push(() => {
      input.removeEventListener("input", live);
      input.removeEventListener("change", done);
    });
  }

  // ---- full screen ---------------------------------------------------------

  /**
   * Full screen is the window's business now: the stage already fills it and
   * macOS owns the green light. These stay so the call sites read the same,
   * and so `this.full` can never be left true by a call with no gesture.
   */
  private async enterFullscreen(): Promise<void> {
    const stage = this.stage;
    if (this.opts.desktop || !stage || this.full) return;
    const native = document.fullscreenEnabled && typeof stage.requestFullscreen === "function";
    if (native) {
      try {
        await stage.requestFullscreen();
        this.full = true;
        this.syncLabels();
        return;
      } catch {
        // Refused. The class below does the same job without permission.
      }
    }
    stage.classList.add("stage--full");
    this.full = true;
    this.measure();
    this.syncLabels();
  }

  private async exitFullscreen(): Promise<void> {
    if (this.opts.desktop) return;
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // Already gone.
      }
    }
    this.stage?.classList.remove("stage--full");
    this.full = false;
    this.measure();
    this.syncLabels();
  }



  // ---- states --------------------------------------------------------------

  private setState(state: State): void {
    this.state = state;
    this.root.dataset.state = state;
    const show = (name: string, on: boolean) => {
      const el = this.ui[name];
      if (el) el.hidden = !on;
    };
    show("title", state === "title");
    show("achievements", state === "achievements");
    show("paint", state === "paint");
    show("settings", state === "settings");
    show("credits", state === "credits");
    show("brief", state === "brief");
    show("pause", state === "paused");
    show("clear", state === "clear");
    show("over", state === "over");
    show("win", state === "win");
    // Behind every sheet but the brief, the yard plays itself.
    this.attract = !this.opts.preview &&
      (state === "title" || state === "achievements" || state === "credits" ||
        state === "paint" || state === "settings");
    this.sound.quiet = this.attract;
    if (!this.attract) this.sound.engine(0);
    show("hud", state === "playing" || state === "paused");
    if (!this.opts.preview) this.sound.music(this.moodFor(state));
    if (this.input) this.input.capture = state === "playing";
    if (state !== "playing") {
      this.input?.reset();
      this.sound.engine(0);
    }
  }

  /** Which loop suits the moment: the level's mood in play, calm on the sheets. */
  private moodFor(state: State): import("./audio.ts").MusicName | null {
    if (state === "playing" || state === "paused" || state === "brief" || state === "clear") {
      const moods: Array<import("./audio.ts").MusicName> = ["drive", "drive", "drive", "drive", "tense", "tense", "industrial", "industrial", "boss", "boss"];
      if (this.mode === "run") {
        if (this.depth % 4 === 0) return "boss";
        return moods[Math.min(moods.length - 1, (this.depth - 1) % 8)] ?? "drive";
      }
      return moods[this.levelIndex] ?? "tense";
    }
    if (state === "over") return null;
    return "calm";
  }

  private brief(): void {
    const level = this.current;
    this.setText(
      "briefNumber",
      this.mode === "run" ? `Depth ${this.depth}` : `Level ${this.levelIndex + 1}`,
    );
    this.setText("briefName", level.name);
    // A hand-made yard's objective is flavour; a generated one's is the rule
    // it is playing by, so it earns its place.
    this.setText("briefObjective", this.mode === "run" ? level.objective : "");
    this.setState("brief");
    this.sound.play("start");
    window.setTimeout(() => {
      if (this.state === "brief") this.setState("playing");
    }, 1500);
  }

  private togglePause(): void {
    if (this.opts.preview) return;
    if (this.state === "playing") this.setState("paused");
    else if (this.state === "paused") this.setState("playing");
  }

  // ---- building a level ----------------------------------------------------

  private loadLevel(index: number): void {
    this.levelIndex = Math.min(index, levels.length - 1);
    this.build(levels[this.levelIndex] as Level);
  }

  /**
   * The next yard of a run: generated from the run's seed and how deep you
   * are, which is also what the difficulty reads, so a generated yard scales
   * exactly as a hand-made one does.
   */
  private loadYard(kind: YardKind): void {
    this.levelIndex = this.depth - 1;
    this.build(generateYard(this.runSeed + this.depth * 7919, this.depth, kind));
  }

  private build(level: Level): void {
    this.current = level;
    const map = this.progress.on("mirror") ? level.map.map((row) => row.split("").reverse().join("")) : level.map;
    const parsed = parseLevel(map);

    this.world = makeWorld();
    this.world.on("begin-contact", (contact) => {
      this.contacts.push([contact.getFixtureA().getBody(), contact.getFixtureB().getBody()]);
    });

    this.particles.clear();
    this.actors = [];
    this.shells = [];
    this.exits = [];
    this.originals = new Set();
    this.reap.clear();
    this.contacts.length = 0;
    this.pending.length = 0;
    this.spawners = parsed.spawners.map((s) => ({ x: s.x, y: s.y }));
    this.mapSize = { w: parsed.cols * TILE, h: parsed.rows * TILE };
    this.cars = [];
    this.factories = [];
    this.roadSet = new Set(parsed.roads.map((r) => `${Math.floor(r.x / TILE)},${Math.floor(r.y / TILE)}`));
    const isRoad = (col: number, row: number) => this.roadSet.has(`${col},${row}`);
    this.roads = parsed.roads.map((r) => {
      const col = Math.floor(r.x / TILE);
      const row = Math.floor(r.y / TILE);
      return { x: r.x, y: r.y, e: isRoad(col + 1, row), w: isRoad(col - 1, row), n: isRoad(col, row - 1), s: isRoad(col, row + 1) };
    });

    for (const w of parsed.walls) {
      makeBox(this.world, w.x, w.y, w.hw, w.hh, base("wall", colours.green, 1, 3), {
        static: true,
        category: CAT.WALL,
        mask: MASK.WALL,
        friction: 0.3,
      });
    }

    for (const t of parsed.tiles) this.makeTile(t.x, t.y, t.ch);
    for (const p of parsed.props) this.makeProp(p.x, p.y, p.ch);
    for (const e of parsed.enemies) this.makeEnemy(e.x, e.y, e.ch, true);
    for (const p of parsed.pickups) this.makePickup(p.x, p.y, PICKUP_BY_CHAR[p.ch] ?? { power: "triple" });
    for (const x of parsed.exits) {
      this.exits.push(
        makeCircle(this.world, x.x, x.y, 1, base("exit", colours.green, 1, 3), {
          static: true,
          sensor: true,
          category: CAT.PICKUP,
          mask: MASK.PICKUP,
        }),
      );
    }

    const tank = this.tank();
    // In a run the tank keeps whatever the last yard left it, so getting
    // through cheaply is worth as much as getting through fast.
    const start = this.mode === "run" && this.carried > 0 ? Math.min(this.carried, this.maxHp()) : this.maxHp();
    this.player = makeBox(
      this.world,
      parsed.player.x,
      parsed.player.y,
      1.05 * tank.size,
      0.72 * tank.size,
      {
        ...base("player", this.hullColour(), this.maxHp(), 3),
        hp: start,
        team: "player",
        // Facing up the page, like the keys: every map starts you at the
        // bottom with the way on ahead.
        turret: NORTH,
        roll: 0,
        style: tank.id,
        paint: { colour: this.hullColour(), decal: this.paintJob.decal },
      },
      {
        category: CAT.PLAYER,
        mask: MASK.PLAYER,
        density: 4,
        friction: 0.5,
        restitution: 0.05,
        linearDamping: 2.4,
        angularDamping: 7,
        angle: NORTH,
      },
    );

    for (const f of parsed.factories) this.makeFactory(f.x, f.y);
    this.remaining = this.actors.length + this.factories.length;
    // A map with no garrison has nothing to kill, and `openExits` is otherwise
    // only reached by killing the last of them, so the way out would stay shut.
    if (this.remaining === 0) this.openExits();
    this.levelTime = 0;
    this.levelScore = 0;
    this.levelTanks = 0;
    this.shield = this.grantShield ? 2 : 0;
    this.shieldTimer = this.grantShield ? 20 : 0;
    this.grantShield = false;
    if (this.grantPower) {
      this.powers[this.grantPower] = this.powerSeconds();
      this.grantPower = null;
    }
    this.drop = null;
    this.dropRest = 6;
    this.dropsLeft = 1;
    this.heat = 0;
    this.combo = 0;
    this.comboTimer = 0;
    this.spawnTimer = 8;
    this.reinforcements = 0;
    this.powers = {};
    this.cooldown = 0;
    this.boostCharge = 1;
    this.boostTimer = 0;
    this.invuln = 1;
    this.shake = 0;
    this.hitStop = 0;
    this.time = 0;
    this.cam.x = parsed.player.x;
    this.cam.y = parsed.player.y;
    this.cam.rot = this.headingCam ? -Math.PI / 2 : 0;
    this.updateHud();
  }

  private makeTile(x: number, y: number, ch: string): void {
    const spec =
      ch === "#"
        ? { fill: colours.concrete, hp: 3 }
        : ch === "="
          ? { fill: colours.brick, hp: 1 }
          : { fill: colours.hedge, hp: 1 };
    const entity: Entity = {
      ...base("tile", spec.fill, spec.hp, 2.5),
      ch,
      crushable: ch === "-",
    };
    makeBox(this.world, x, y, TILE / 2 - 0.02, TILE / 2 - 0.02, entity, {
      static: true,
      category: CAT.WALL,
      mask: MASK.WALL,
      friction: 0.4,
    });
  }

  private makeProp(x: number, y: number, ch: string): void {
    if (ch === "B") {
      makeCircle(this.world, x, y, 0.55, { ...base("barrel", colours.car, 1, 2.5), ch }, {
        category: CAT.PROP,
        mask: MASK.PROP,
        density: 1.2,
        friction: 0.5,
        restitution: 0.2,
        linearDamping: 1.6,
        angularDamping: 2,
      });
      return;
    }
    const heavy = ch === "v" || ch === "V";
    const driving = ch === "V";
    // A driven car starts along whichever way its road runs.
    const col = Math.floor(x / TILE);
    const row = Math.floor(y / TILE);
    // A car sets off along whichever way its street runs.
    const lane = driving && this.isRoad(col, row + 1) && !this.isRoad(col + 1, row) ? 1 : 0;
    const body = makeBox(
      this.world,
      x,
      y,
      heavy ? 0.95 : 0.6,
      heavy ? 0.5 : 0.6,
      { ...base("prop", heavy ? colours.car : colours.crate, heavy ? 4 : 2, 2.5), ch, route: driving, lane, throttle: driving ? 1 : 0 },
      {
        category: CAT.PROP,
        mask: MASK.PROP,
        density: heavy ? 3 : 1,
        friction: 0.6,
        restitution: 0.05,
        linearDamping: heavy ? 2.4 : 1.6,
        angularDamping: heavy ? 4 : 2.4,
        angle: driving && lane === 1 ? Math.PI / 2 : 0,
      },
    );
    if (driving) this.cars.push(body);
  }

  private isRoad = (col: number, row: number): boolean => this.roadSet.has(`${col},${row}`);

  /**
   * The traffic. `roadStep` decides where each car is going; this drives it
   * there, keeps it in its lane, and stops it shunting whatever is in front.
   */
  private driveCars(dt: number): void {
    for (const car of this.cars) {
      const e = entityOf(car);
      if (!e || e.dead) continue;
      const pos = car.getPosition();
      const col = Math.floor(pos.x / TILE);
      const row = Math.floor(pos.y / TILE);
      // At a junction not every car turns the same way, so the streets do
      // not fall into lockstep.
      const step = roadStep(this.isRoad, col, row, e.lane ?? 0, (open) =>
        (Math.random() < 0.35 ? open[1] : open[0]) as number
      );
      e.lane = step.lane;

      const target = tileCentre(step.col, step.row);
      const angle = car.getAngle();
      const err = wrap(Math.atan2(target.y - pos.y, target.x - pos.x) - angle);
      car.applyTorque(clamp(err * 2.6, -1, 1) * 26 * car.getInertia(), true);

      // Anything solid close ahead, and it waits rather than shunts.
      const look = new Vec2(pos.x + Math.cos(angle) * 2.4, pos.y + Math.sin(angle) * 2.4);
      e.wait = feel(this.world, pos, look, car) < 0.55 ? 0.45 : Math.max(0, (e.wait ?? 0) - dt);
      const going = (e.wait ?? 0) <= 0 && Math.abs(err) < 0.9;
      const speed = car.getLinearVelocity().length();
      const push = going && speed < 8 ? 11 * car.getMass() : 0;
      car.applyForceToCenter(new Vec2(Math.cos(angle) * push, Math.sin(angle) * push), true);
      // Keep it in its lane: kill the sideways slide.
      const local = car.getLocalVector(car.getLinearVelocity());
      car.setLinearVelocity(car.getWorldVector(new Vec2(local.x, local.y * 0.25)));
      e.throttle = going ? 1 : 0;
      e.speed = speed;
    }
  }

  /**
   * The works. It stands there building tanks on a clock, and the way out
   * stays shut while one is still up, so a yard with factories is a race
   * to the buildings rather than a hunt for the last straggler.
   */
  private makeFactory(x: number, y: number): void {
    const body = makeBox(
      this.world,
      x,
      y,
      1.4,
      1.4,
      { ...base("factory", "#6e2419", 8 + this.tough() * 3, 3.5), team: "enemy", style: "factory", ttl: 5 },
      { static: true, category: CAT.ENEMY, mask: MASK.ENEMY },
    );
    this.factories.push(body);
    this.originals.add(body);
  }

  /** Each works puts another one out, so long as the yard is not already full. */
  private tickFactories(dt: number): void {
    for (const works of this.factories) {
      const e = entityOf(works);
      if (!e || e.dead) continue;
      e.ttl = (e.ttl ?? 0) - dt;
      if ((e.ttl ?? 0) > 0) continue;
      const cap = 3 + Math.floor(this.stars() / 2);
      if (this.reinforcements >= cap) {
        e.ttl = 1.5;
        continue;
      }
      const pos = works.getPosition();
      const stars = this.stars();
      const kind = stars >= 4 && Math.random() < 0.4 ? "H" : stars >= 2 && Math.random() < 0.25 ? "D" : "E";
      // Out of the door, into whatever room there is beside it.
      for (let tries = 0; tries < 8; tries++) {
        const a = Math.random() * Math.PI * 2;
        const x = pos.x + Math.cos(a) * 3.2;
        const y = pos.y + Math.sin(a) * 3.2;
        if (x < TILE || y < TILE || x > this.mapSize.w - TILE || y > this.mapSize.h - TILE) continue;
        if (blast(this.world, x, y, 1.2).length) continue;
        this.makeEnemy(x, y, kind, false);
        this.reinforcements += 1;
        this.particles.ring(x, y, 2, colours.car, 0.6);
        this.sound.play("alert", 0.4);
        break;
      }
      e.ttl = Math.max(3.5, 8 - this.levelIndex * 0.25);
    }
  }

  /** The garrison hardens as the yards go on: extra hits, later on. */
  private tough(): number {
    return this.levelIndex >= 8 ? 2 : this.levelIndex >= 5 ? 1 : 0;
  }

  private makeEnemy(x: number, y: number, ch: string, original: boolean): Body {
    let body: Body;
    const extra = this.tough();
    const mind = { mode: "patrol" as const, modeTime: 9 };
    if (ch === "T" || ch === "M") {
      body = makeCircle(
        this.world,
        x,
        y,
        ch === "M" ? 0.85 : 0.78,
        { ...base("turret", ch === "M" ? colours.boss : colours.enemy, 3 + extra, 3), team: "enemy", turret: 0, recoil: 0, style: ch === "M" ? "mortar" : "turret" },
        { static: true, category: CAT.ENEMY, mask: MASK.ENEMY },
      );
    } else if (ch === "Z") {
      body = makeBox(
        this.world,
        x,
        y,
        1.9,
        1.35,
        { ...base("boss", colours.boss, 24 + extra * 4, 4), team: "enemy", turret: 0, roll: 0, phase: 0, style: "boss", mode: "hunt" },
        { category: CAT.ENEMY, mask: MASK.ENEMY, density: 9, friction: 0.5, linearDamping: 3, angularDamping: 9 },
      );
    } else if (ch === "D") {
      body = makeCircle(
        this.world,
        x,
        y,
        0.45,
        { ...base("enemy", colours.enemy, 1, 2.5), team: "enemy", turret: 0, roll: 0, style: "drone", ...mind },
        { category: CAT.ENEMY, mask: MASK.ENEMY, density: 1.5, friction: 0.2, linearDamping: 1.2, angularDamping: 2 },
      );
    } else if (ch === "H") {
      body = makeBox(
        this.world,
        x,
        y,
        0.8,
        0.55,
        { ...base("enemy", "#a13a2c", 2 + extra, 3), team: "enemy", turret: 0, roll: 0, style: "hunter", ...mind },
        { category: CAT.ENEMY, mask: MASK.ENEMY, density: 2.6, friction: 0.5, linearDamping: 2.2, angularDamping: 6 },
      );
    } else if (ch === "G") {
      body = makeBox(
        this.world,
        x,
        y,
        1.15,
        0.8,
        { ...base("enemy", "#6e2419", 5 + extra, 3.5), team: "enemy", turret: 0, roll: 0, style: "gunner", ...mind },
        { category: CAT.ENEMY, mask: MASK.ENEMY, density: 5, friction: 0.5, linearDamping: 3, angularDamping: 8 },
      );
    } else {
      body = makeBox(
        this.world,
        x,
        y,
        0.95,
        0.66,
        { ...base("enemy", colours.enemy, 3 + extra, 3), team: "enemy", turret: 0, roll: 0, style: "tank", ...mind },
        { category: CAT.ENEMY, mask: MASK.ENEMY, density: 3.4, friction: 0.5, linearDamping: 2.6, angularDamping: 7 },
      );
    }
    this.actors.push(body);
    if (original) this.originals.add(body);
    return body;
  }

  private makePickup(x: number, y: number, what: { power?: PowerName; perk?: Perk }, fuse?: number): Body {
    return makeCircle(
      this.world,
      x,
      y,
      0.55,
      { ...base("pickup", colours.yellow, 1, 2.5), ...what, ...(fuse ? { fuse, ttl: fuse } : {}) },
      { static: true, sensor: true, category: CAT.PICKUP, mask: MASK.PICKUP },
    );
  }

  /**
   * Help, when it is needed and not before. Two armour or less and a repair
   * is dropped somewhere a short drive away, on a fuse. Reach it in time or
   * it is gone, and the next one is a while coming.
   */
  private considerDrop(dt: number): void {
    this.dropRest = Math.max(0, this.dropRest - dt);
    if (this.drop) {
      const e = entityOf(this.drop);
      if (!e || e.dead) {
        this.drop = null;
        this.dropRest = DROP_REST;
        return;
      }
      e.ttl = (e.ttl ?? 0) - dt;
      if (e.ttl <= 0) {
        const p = this.drop.getPosition();
        this.particles.puff(p.x, p.y, 4, "#6d6a60");
        this.kill(this.drop, e, false);
        this.drop = null;
        this.dropRest = DROP_REST;
      }
      return;
    }
    const player = this.player;
    const pe = player && entityOf(player);
    if (!pe || pe.dead || pe.hp > 1 || pe.hp >= this.maxHp() || this.dropRest > 0 || this.dropsLeft <= 0) return;
    const from = player.getPosition();
    for (let tries = 0; tries < 24; tries++) {
      const a = Math.random() * Math.PI * 2;
      const d = 9 + Math.random() * 6;
      const x = from.x + Math.cos(a) * d;
      const y = from.y + Math.sin(a) * d;
      if (x < TILE * 1.5 || y < TILE * 1.5 || x > this.mapSize.w - TILE * 1.5 || y > this.mapSize.h - TILE * 1.5) continue;
      if (blast(this.world, x, y, 1.3).length) continue;
      this.drop = this.makePickup(x, y, { perk: "repair" }, DROP_FUSE);
      this.dropsLeft -= 1;
      this.particles.ring(x, y, 2.6, colours.car, 0.8);
      this.particles.score(x, y, "REPAIR");
      this.sound.play("pickup", 0.6);
      return;
    }
  }

  // ---- the loop ------------------------------------------------------------

  private frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    const real = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;

    if (this.state === "playing" || this.attract) {
      let scale = 1;
      if (this.hitStop > 0) {
        this.hitStop = Math.max(0, this.hitStop - real);
        scale = 0.16;
      }
      this.accumulator += real * scale;
      let steps = 0;
      while (this.accumulator >= STEP && steps < 5) {
        this.step(STEP);
        this.accumulator -= STEP;
        steps += 1;
      }
      if (steps === 5) this.accumulator = 0;
    } else {
      this.accumulator = 0;
      if (!this.opts.preview) this.driftCamera(real);
      this.particles.step(real);
    }

    this.time += real;
    this.decayShake(real);
    this.draw();
  };

  private step(dt: number): void {
    this.levelTime += dt;
    this.input?.update();
    this.intent = this.opts.preview || this.attract
      ? this.autopilot()
      : this.rotated(this.input?.intent ?? null);

    this.drivePlayer(dt);
    for (const body of this.actors) this.driveEnemy(body, dt);
    this.driveCars(dt);
    this.ageShells(dt);

    const before = SHELLS_CANCEL
      ? this.shells.map((s) => {
        const p = s.getPosition();
        return { x: p.x, y: p.y };
      })
      : [];
    this.world.step(dt, 8, 3);
    if (SHELLS_CANCEL) this.clashShells(before);

    this.resolveContacts();
    this.applyBlasts();
    this.collect();

    this.tickTimers(dt);
    this.tickFactories(dt);
    this.particles.step(dt);
    this.followCamera(dt);
    this.updateHud();
  }

  // ---- the player ----------------------------------------------------------

  private drivePlayer(dt: number): void {
    const body = this.player;
    const e = body && entityOf(body);
    if (!body || !e || e.dead) return;

    const intent = this.intent;
    if (!intent) return;

    const angle = body.getAngle();
    const mass = body.getMass();
    let throttle = intent.throttle;

    // Two ways to steer. A keyboard turns the hull; a thumb or a stick says
    // where to go and the hull works it out, because nobody wants to
    // countersteer with a thumb.
    if (intent.turnTo) {
      const want = Math.atan2(intent.turnTo.y, intent.turnTo.x);
      const err = wrap(want - angle);
      body.applyTorque(clamp(err * 2.6, -1, 1) * 34 * this.tank().turn * body.getInertia(), true);
      // Do not drive full tilt while still swinging round.
      throttle *= Math.max(0.25, 1 - Math.abs(err) / 2.4);
    } else if (intent.steer !== 0) {
      const reverse = throttle < -0.1 ? -1 : 1;
      body.applyTorque(intent.steer * reverse * 26 * this.tank().turn * body.getInertia(), true);
    }

    const boosting = intent.boost && this.boostCharge > 0.2 && throttle > 0;
    if (boosting) {
      this.boostCharge = Math.max(0, this.boostCharge - dt / 1.3);
      this.boostTimer = 0.35;
    }

    const turbo = (this.progress.on("turbo") ? 1.35 : 1) * this.tank().drive;
    const tuned = (1 + 0.08 * this.stats.speed) * (this.trades.includes("foot") ? 1.25 : 1) * (this.trades.includes("match") ? 0.9 : 1);
    const drive = throttle * (boosting ? 46 : 27) * turbo * tuned * mass;
    e.throttle = throttle;
    e.speed = body.getLinearVelocity().length();
    body.applyForceToCenter(new Vec2(Math.cos(angle) * drive, Math.sin(angle) * drive), true);
    this.grip(body, 0.86);
    this.aim(body, e, dt);
    this.tracks(body, e, dt);

    if (intent.fire) this.fire(body, e);

    const speed = body.getLinearVelocity().length();
    if (!this.opts.preview) this.sound.engine(Math.min(1, speed / 13 + Math.abs(throttle) * 0.35));
  }

  /** Kill the sideways part of the velocity, so it tracks instead of sliding. */
  private grip(body: Body, amount: number): void {
    const v = body.getLinearVelocity();
    const local = body.getLocalVector(v);
    const killed = new Vec2(local.x, local.y * (1 - amount));
    body.setLinearVelocity(body.getWorldVector(killed));
  }

  private aim(body: Body, e: Entity, dt: number): void {
    const want = this.wantAim(body);
    const err = wrap(want - (e.turret ?? 0));
    e.turret = (e.turret ?? 0) + clamp(err, -14 * dt, 14 * dt);
  }

  /**
   * Where the gun wants to point. Locked aim keeps it on the nose: turn the
   * tank to aim. (With `LOCKED_ARC` on, the mouse or right thumb could nudge
   * it along a short arc either side; that is parked, see below.)
   */
  private wantAim(body: Body): number {
    const intent = this.intent;
    const hull = body.getAngle();
    let want: number | null = null;
    if (intent?.aimDir) {
      want = Math.atan2(intent.aimDir.y, intent.aimDir.x);
    } else if (intent?.pointer) {
      const p = screenToWorld(this.cam, this.view, intent.pointer.x, intent.pointer.y);
      const pos = body.getPosition();
      want = Math.atan2(p.y - pos.y, p.x - pos.x);
    }
    if (this.aimLocked && !this.opts.preview) {
      if (!LOCKED_ARC || want === null) return hull;
      return hull + clamp(wrap(want - hull), -AIM_ARC, AIM_ARC);
    }
    return want ?? hull;
  }

  /** Stamp track marks every so often, so the map remembers where you drove. */
  private tracks(body: Body, e: Entity, dt: number): void {
    const speed = body.getLinearVelocity().length();
    e.roll = ((e.roll ?? 0) + speed * dt * 0.7) % 2;
    if (speed < 1.2 || this.opts.reduced) return;
    const angle = body.getAngle();
    const nx = -Math.sin(angle);
    const ny = Math.cos(angle);
    const pos = body.getPosition();
    const gap = (e.hh ?? 0.7) + 0.04;
    if (Math.random() < speed * dt * 1.6) {
      this.particles.tread(pos.x + nx * gap, pos.y + ny * gap, angle);
      this.particles.tread(pos.x - nx * gap, pos.y - ny * gap, angle);
    }
  }

  // ---- guns ----------------------------------------------------------------

  /** The player's trigger. Everything else calls `shoot` on its own schedule. */
  private fire(body: Body, e: Entity): void {
    if (this.cooldown > 0) return;
    const weapon = this.loadout();
    this.cooldown = weapon.cooldown;
    this.shoot(body, e, weapon, "player");
    if (weapon.name === "Kitchen Sink") this.note((st) => void (st.sink = true));
  }

  private shoot(body: Body, e: Entity, weapon: Weapon, team: Team): void {
    const angle = e.turret ?? body.getAngle();
    const pos = body.getPosition();
    const reach = (e.hw ?? 1) + 0.7;
    e.recoil = 1;

    for (let i = 0; i < weapon.shots; i++) {
      const spread = (i - (weapon.shots - 1) / 2) * weapon.spread;
      const a = angle + spread;
      const dir = new Vec2(Math.cos(a), Math.sin(a));
      const from = new Vec2(pos.x + dir.x * reach, pos.y + dir.y * reach);
      const shell = makeCircle(
        this.world,
        from.x,
        from.y,
        weapon.radius,
        {
          ...base("shell", team === "player" ? colours.ink : colours.enemy, 1, 2),
          team,
          damage: weapon.damage,
          bounces: weapon.bounces,
          ttl: 2.4,
          r: weapon.radius,
        },
        {
          category: team === "player" ? CAT.PSHELL : CAT.ESHELL,
          mask: team === "player" ? MASK.PSHELL : MASK.ESHELL,
          density: 8,
          friction: 0,
          restitution: weapon.bounces > 0 ? 0.97 : 0.05,
          bullet: true,
          linearDamping: 0,
          angularDamping: 0,
        },
      );
      const entity = entityOf(shell);
      if (entity) entity.blast = weapon.blast;
      shell.setLinearVelocity(Vec2.mul(dir, weapon.speed));
      this.shells.push(shell);
      this.particles.sparks(from.x, from.y, dir.x, dir.y, 4);
    }

    // Recoil, so a shot moves the thing that fired it.
    const dir = new Vec2(Math.cos(angle), Math.sin(angle));
    body.applyLinearImpulse(
      Vec2.mul(dir, -weapon.damage * 3.2 * body.getMass()),
      body.getWorldCenter(),
      true,
    );
    this.particles.ring(
      pos.x + dir.x * reach,
      pos.y + dir.y * reach,
      0.5 + weapon.radius * 2,
      colours.acid,
      0.22,
    );
    this.sound.play(weapon.blast > 0 ? "heavy" : "fire", team === "player" ? 1 : 0.55);
    if (team === "player") this.addShake(weapon.blast > 0 ? 7 : 3.5);

    if (this.shells.length > 46) {
      const old = this.shells.shift();
      if (old) this.kill(old, entityOf(old), false);
    }
  }

  private ageShells(dt: number): void {
    for (const shell of this.shells) {
      const e = entityOf(shell);
      if (!e || e.dead) continue;
      e.ttl = (e.ttl ?? 0) - dt;
      if (e.ttl <= 0) {
        if (e.mortar) {
          const p = shell.getPosition();
          this.pending.push({ x: p.x, y: p.y, radius: e.blast ?? 2.8, damage: 1, power: 8 });
        }
        this.kill(shell, e, false);
        continue;
      }
      const p = shell.getPosition();
      if (!this.opts.reduced && Math.random() < 0.5) {
        this.particles.spawn("smoke", p.x, p.y, {
          life: 0.22,
          size: 0.12,
          colour: e.team === "player" ? "#6d6a60" : "#8a2f26",
        });
      }
    }
    this.shells = this.shells.filter((s) => !entityOf(s)?.dead);
  }

  /**
   * Shells from opposite sides that meet in the air knock each other out.
   * Box2D will not do it: fast bodies do not sweep against each other, so
   * two shells swap places inside a step and never touch. A mortar round is
   * up in the air and passes over everything, so it is left alone. A heavy
   * shell still goes off where it was stopped.
   */
  private clashShells(before: ReadonlyArray<{ x: number; y: number }>): void {
    const n = Math.min(this.shells.length, before.length);
    for (let i = 0; i < n; i++) {
      const a = this.shells[i]!;
      const ea = entityOf(a);
      if (!ea || ea.dead || ea.mortar) continue;
      for (let j = i + 1; j < n; j++) {
        const b = this.shells[j]!;
        const eb = entityOf(b);
        if (!eb || eb.dead || eb.mortar || eb.team === ea.team) continue;
        const pa = a.getPosition();
        const pb = b.getPosition();
        if (!sweptHit(before[i]!, pa, before[j]!, pb, (ea.r ?? 0.2) + (eb.r ?? 0.2) + 0.15)) continue;
        const x = (pa.x + pb.x) / 2;
        const y = (pa.y + pb.y) / 2;
        for (const e of [ea, eb]) {
          if ((e.blast ?? 0) > 0) this.pending.push({ x, y, radius: (e.blast ?? 3) * 0.7, damage: 1, power: 7 });
        }
        this.particles.ring(x, y, 0.9, colours.yellow, 0.3);
        this.particles.sparks(x, y, 1, 0, 4);
        this.particles.sparks(x, y, -1, 0, 4);
        this.sound.play("ricochet", 0.45);
        this.kill(a, ea, false);
        this.kill(b, eb, false);
        break;
      }
    }
  }

  // ---- the other side ------------------------------------------------------

  /**
   * The other side's mind. Four modes: patrolling between points of its own
   * choosing, hunting once it has a line of sight, searching the last place
   * it saw the player once that line is lost, and fleeing for a moment when
   * something goes off next to it. Everything aims first and shoots down a
   * clear line; the mortar is the exception and does not need one.
   */
  private driveEnemy(body: Body, dt: number): void {
    const e = entityOf(body);
    const player = this.player;
    const pe = player && entityOf(player);
    if (!e || e.dead || !player || !pe || pe.dead) return;

    e.recoil = Math.max(0, (e.recoil ?? 0) - dt * 4);
    e.ttl = Math.max(0, (e.ttl ?? 0) - dt);
    e.modeTime = (e.modeTime ?? 0) + dt;
    e.speed = body.getLinearVelocity().length();

    if ((e.stun ?? 0) > 0) {
      e.stun = (e.stun ?? 0) - dt;
      e.throttle = 0;
      return;
    }

    const pos = body.getPosition();
    const target = player.getPosition();
    const away = Vec2.sub(target, pos);
    const distance = away.length();
    const want = Math.atan2(away.y, away.x);
    const style = e.style ?? "tank";

    const onEdge = this.levelTime < this.alertUntil ? 1.25 : 1;
    const range = (style === "turret" ? 23 : style === "mortar" ? 26 : style === "boss" ? 26 : style === "gunner" ? 22 : style === "hunter" ? 20 : style === "drone" ? 30 : 21) * onEdge;
    // A turret only sees where its gun points: a cone, swept back and forth
    // when there is nothing in it. Come at it from behind.
    const inCone = style !== "turret" || e.mode === "hunt" || Math.abs(wrap(want - (e.turret ?? 0))) < 0.8 * onEdge;
    const canSee = distance < range && inCone && (style === "mortar" || style === "drone" || lineOfSight(this.world, pos, target, { ignore: body, team: "enemy" }));
    // An engine at full tilt can be heard through a wall.
    const playerSpeed = player.getLinearVelocity().length();
    const canHear = !canSee && distance < 11 && playerSpeed > 4;

    // The mind.
    if (canSee) {
      if (e.mode !== "hunt") {
        e.mode = "hunt";
        e.modeTime = 0;
        if (e.kind !== "turret" && this.time - this.lastAlert > 1.2) {
          this.lastAlert = this.time;
          this.sound.play("alert", 0.6);
          this.particles.score(pos.x, pos.y - 1, "!");
        }
        // Tell the others nearby where to look.
        for (const other of this.actors) {
          const oe = entityOf(other);
          if (!oe || oe === e || oe.dead || oe.mode === "hunt" || oe.kind === "turret") continue;
          if (Vec2.distance(other.getPosition(), pos) > 16) continue;
          oe.mode = "search";
          oe.seen = { x: target.x, y: target.y };
          oe.goal = oe.seen;
          oe.modeTime = 0;
        }
      }
      e.seen = { x: target.x, y: target.y };
    } else if (canHear && e.kind !== "turret") {
      if (e.mode === "patrol" || e.mode === "search") {
        if (e.mode !== "search") e.modeTime = 0;
        e.mode = "search";
        e.seen = { x: target.x, y: target.y };
        e.goal = e.seen;
      }
    } else if (e.mode === "hunt") {
      e.mode = "search";
      e.modeTime = 0;
      e.goal = e.seen;
    }

    // Hurt and outmatched, a tank backs off to find its friends.
    if (e.kind === "enemy" && style !== "drone" && e.hp <= 1 && e.maxHp > 1 && e.mode !== "flee" && canSee && distance < 9) {
      e.mode = "flee";
      e.goal = { x: target.x, y: target.y };
      e.modeTime = 0;
    }

    // Aim. Down the line to the player if there is one, a little ahead of
    // it by how long the shell will take, else where it is going.
    const slew = style === "turret" ? 1.9 : style === "mortar" ? 1.2 : style === "boss" ? 2.2 : style === "hunter" ? 3.4 : style === "gunner" ? 1.8 : 2.8;
    const shellSpeed = style === "boss" ? 26 : style === "gunner" ? 20 : style === "hunter" ? 30 : 24;
    const pv = player.getLinearVelocity();
    const flight = distance / shellSpeed;
    const lead = new Vec2(target.x + pv.x * flight * 0.85, target.y + pv.y * flight * 0.85);
    const wantLead = Math.atan2(lead.y - pos.y, lead.x - pos.x);
    let aimAt = body.getAngle();
    if (canSee) aimAt = wantLead;
    else if (e.mode === "search" && e.seen) aimAt = Math.atan2(e.seen.y - pos.y, e.seen.x - pos.x);
    else if (style === "turret") {
      // The sweep: a slow arc either side of where it was set, with a pause
      // at each end, like a guard turning his head.
      if (e.goal === undefined) e.goal = { x: e.turret ?? 0, y: 0 };
      const sweep = Math.sin(e.modeTime * 0.55);
      aimAt = e.goal.x + Math.sign(sweep) * Math.pow(Math.abs(sweep), 0.6) * 1.3;
    } else if (e.mode === "patrol") aimAt = body.getAngle() + Math.sin(e.modeTime * 0.9) * 0.9;
    const err = wrap(aimAt - (e.turret ?? 0));
    e.turret = (e.turret ?? 0) + clamp(err, -slew * dt, slew * dt);

    if (e.kind === "turret" && e.mode === "search" && e.modeTime > 6) {
      e.mode = "patrol";
      e.modeTime = 0;
    }

    if (style === "mortar") {
      if (canSee && (e.ttl ?? 0) <= 0 && distance > 5) {
        this.lob(body, e, target);
        e.ttl = 3.6;
      }
      return;
    }

    if (style !== "drone" && canSee && Math.abs(wrap(wantLead - (e.turret ?? 0))) < 0.16 && (e.ttl ?? 0) <= 0) {
      const phase = e.phase ?? 0;
      const weapon: Weapon =
        style === "boss"
          ? { ...WEAPONS.triple, speed: 26, damage: 1, shots: 2 + phase, spread: 0.22 }
          : style === "gunner"
            ? { ...WEAPONS.heavy, speed: 20, damage: 1, blast: 2.6, radius: 0.3 }
            : style === "hunter"
              ? { ...WEAPONS.cannon, speed: 30, damage: 1, radius: 0.15 }
              : { ...WEAPONS.cannon, speed: 24, damage: 1 };
      this.shoot(body, e, weapon, "enemy");
      // They reload quicker the further in you are.
      const brisk = 1 - Math.min(0.25, this.levelIndex * 0.03);
      e.ttl = (style === "turret" ? 1.7 : style === "boss" ? 1.5 - phase * 0.35 : style === "gunner" ? 2.8 : style === "hunter" ? 0.9 : 1.35) * brisk;
    }

    if (e.kind === "turret") return;

    // Drive.
    const angle = body.getAngle();
    const mass = body.getMass();
    const speed = style === "boss" ? 15 + (e.phase ?? 0) * 5 : style === "hunter" ? 24 : style === "gunner" ? 11 : style === "drone" ? 30 : 17;
    const hold = style === "boss" ? 9 : style === "gunner" ? 10 : style === "hunter" ? 6 : style === "drone" ? 2 : 8;

    let heading = angle;
    e.dive = false;
    let closing = 0;
    if (e.mode === "flee" && e.goal) {
      heading = Math.atan2(pos.y - e.goal.y, pos.x - e.goal.x);
      closing = 1;
      if (e.modeTime > 1.3) {
        e.mode = "patrol";
        e.modeTime = 99;
        e.goal = undefined;
      }
    } else if (e.mode === "hunt" || style === "boss") {
      heading = want;
      closing = distance > hold ? 1 : distance < hold * 0.7 ? -0.6 : 0.2;
      // At the hold, do not sit still: slide round the player, so a shell
      // aimed at where it was finds nothing.
      if (distance <= hold * 1.3 && style !== "drone" && style !== "gunner") {
        const side = (seedOf(e) % 2 === 0 ? 1 : -1) * (Math.sin(e.modeTime * 0.6) > 0 ? 1 : -1);
        heading = want + side * 1.1;
        closing = 0.7;
      }
      if (style === "drone") {
        // Circle in, then dive. The dive is the ram.
        const dive = Math.sin(e.modeTime * 1.7) > 0.55;
        e.dive = dive;
        heading = dive ? want : want + 0.95;
        closing = 1;
      }
    } else if (e.mode === "search" && e.goal) {
      heading = Math.atan2(e.goal.y - pos.y, e.goal.x - pos.x);
      closing = 0.8;
      if (Vec2.distance(pos, new Vec2(e.goal.x, e.goal.y)) < 2.5 || e.modeTime > 7) {
        e.mode = "patrol";
        e.modeTime = 99;
        e.goal = undefined;
      }
    } else {
      // Patrol: long legs across open ground, leaning toward where the
      // player was last seen by anyone, at an easy pace.
      if (!e.goal || Vec2.distance(pos, new Vec2(e.goal.x, e.goal.y)) < 2 || e.modeTime > 12) {
        e.goal = this.wanderPoint(pos, e.seen);
        e.modeTime = 0;
      }
      heading = Math.atan2(e.goal.y - pos.y, e.goal.x - pos.x);
      closing = 0.6;
    }

    // One ray ahead, so nothing grinds along a wall it could go round.
    const ahead = new Vec2(pos.x + Math.cos(angle) * 5, pos.y + Math.sin(angle) * 5);
    const clear = feel(this.world, pos, ahead, body);
    if (clear < 0.6 && style !== "drone") {
      const left = feel(this.world, pos, offset(pos, angle - 0.8, 4), body);
      const right = feel(this.world, pos, offset(pos, angle + 0.8, 4), body);
      heading = angle + (left > right ? -0.9 : 0.9);
    }

    const steerErr = wrap(heading - angle);
    body.applyTorque(clamp(steerErr * 2.2, -1, 1) * (style === "drone" ? 40 : 24) * body.getInertia(), true);
    const push = closing * speed * mass * Math.max(0.3, 1 - Math.abs(steerErr) / 2.2);
    body.applyForceToCenter(new Vec2(Math.cos(angle) * push, Math.sin(angle) * push), true);
    this.grip(body, style === "drone" ? 0.3 : 0.8);
    e.throttle = closing;
    e.roll = ((e.roll ?? 0) + body.getLinearVelocity().length() * dt * 0.7) % 2;
  }

  /**
   * A free spot a good drive away, for a patrol. Of a handful of candidates
   * the one with the clearest line from here wins, so patrols run along
   * open ground rather than into the nearest wall; and with a last sighting
   * to go on, the candidates lean that way.
   */
  private wanderPoint(from: Vec2, toward?: { x: number; y: number }): { x: number; y: number } {
    let best: { x: number; y: number } | null = null;
    let bestClear = -1;
    for (let tries = 0; tries < 12; tries++) {
      let a = Math.random() * Math.PI * 2;
      if (toward && Math.random() < 0.5) a = Math.atan2(toward.y - from.y, toward.x - from.x) + (Math.random() - 0.5) * 1.2;
      const d = 8 + Math.random() * 10;
      const x = from.x + Math.cos(a) * d;
      const y = from.y + Math.sin(a) * d;
      if (x < TILE * 1.5 || y < TILE * 1.5 || x > this.mapSize.w - TILE * 1.5 || y > this.mapSize.h - TILE * 1.5) continue;
      if (blast(this.world, x, y, 1.4).length) continue;
      const clear = feel(this.world, from, new Vec2(x, y));
      if (clear > bestClear) {
        bestClear = clear;
        best = { x, y };
      }
      if (clear >= 1) break;
    }
    return best ?? { x: from.x, y: from.y };
  }

  /**
   * A mortar round: slow, over everything, and it goes off where the player
   * was when it left. The time in the air is the distance, so a moving tank
   * is a missed tank.
   */
  private lob(body: Body, e: Entity, target: Vec2): void {
    const pos = body.getPosition();
    const dir = Vec2.sub(target, pos);
    const distance = dir.length();
    dir.normalize();
    const speed = 13;
    const shell = makeCircle(
      this.world,
      pos.x + dir.x * 1.1,
      pos.y + dir.y * 1.1,
      0.3,
      { ...base("shell", colours.boss, 1, 2), team: "enemy", damage: 1, bounces: 0, ttl: distance / speed, r: 0.3, mortar: true, blast: 2.8 },
      { category: CAT.ESHELL, mask: CAT.PLAYER, density: 8, friction: 0, restitution: 0, bullet: false, linearDamping: 0, angularDamping: 0 },
    );
    shell.setLinearVelocity(Vec2.mul(dir, speed));
    this.shells.push(shell);
    e.recoil = 1;
    this.particles.ring(pos.x, pos.y, 1.2, colours.acid, 0.3);
    this.particles.puff(pos.x, pos.y, 3, "#4a4a42");
    this.sound.play("heavy", 0.5);
  }

  // ---- what hit what -------------------------------------------------------

  private resolveContacts(): void {
    for (const [a, b] of this.contacts) {
      this.touch(a, b);
      this.touch(b, a);
    }
    this.contacts.length = 0;
  }

  private touch(a: Body, b: Body): void {
    const ea = entityOf(a);
    const eb = entityOf(b);
    if (!ea || !eb || ea.dead || eb.dead) return;

    if (ea.kind === "shell") {
      // A bouncer keeps going off scenery until its ricochets run out.
      const scenery = eb.kind === "wall" || eb.kind === "tile";
      if (scenery && (ea.bounces ?? 0) > 0) {
        ea.bounces = (ea.bounces ?? 0) - 1;
        const p = a.getPosition();
        this.particles.sparks(p.x, p.y, 0, -1, 3);
        // Once a shell has come off a wall it is nobody's: it can hit the
        // tank that fired it. That is the price of spraying ricochets. Both
        // sides have to agree before Box2D lets them touch, which is why
        // this is its own category rather than a wider mask on the shell.
        if (ea.team === "player" && !ea.stray) {
          ea.stray = true;
          ea.fill = colours.car;
          for (let f = a.getFixtureList(); f; f = f.getNext()) {
            f.setFilterData({ groupIndex: 0, categoryBits: CAT.STRAY, maskBits: MASK.STRAY });
          }
        }
        if (ea.team === "player") this.ricochets += 1;
        // A heavy shell that bounces goes off a little every time it lands.
        if ((ea.blast ?? 0) > 0) this.pending.push({ x: p.x, y: p.y, radius: (ea.blast ?? 3) * 0.55, damage: 1, power: 5 });
        this.sound.play("ricochet", 0.6);
        if (eb.kind === "tile") this.hurt(b, eb, ea.damage ?? 1);
        return;
      }
      const p = a.getPosition();
      if ((ea.blast ?? 0) > 0) this.pending.push({ x: p.x, y: p.y, radius: ea.blast ?? 3, damage: 2, power: 9 });
      else {
        this.particles.chips(p.x, p.y, eb.fill, 4, 5);
        this.particles.ring(p.x, p.y, 0.6, colours.acid, 0.24);
      }
      if (eb.kind !== "wall") {
        const self = ea.stray && eb.kind === "player";
        this.hurt(b, eb, self ? Math.min(2, ea.damage ?? 1) : ea.damage ?? 1);
      }
      else this.sound.play("hit", 0.6);
      this.kill(a, ea, false);
      return;
    }

    // A hedge is scenery you go through rather than round.
    if (eb.crushable && (ea.kind === "player" || ea.kind === "enemy" || ea.kind === "boss")) {
      this.hurt(b, eb, 99, ea.kind === "player");
      return;
    }

    // A car under a tank is a flat car. The tank barely notices.
    if (ea.kind === "player" && eb.kind === "prop" && (eb.ch === "v" || eb.ch === "V")) {
      if (a.getLinearVelocity().length() > 2.5) {
        this.crush(b, eb);
        return;
      }
    }

    // A drone is the ram. It is spent on it.
    if (ea.kind === "enemy" && ea.style === "drone" && eb.kind === "player") {
      this.hurt(b, eb, 1, false);
      this.kill(a, ea, false);
      return;
    }

    // Ramming. Worth doing, not worth relying on.
    if (ea.kind === "player" && (eb.kind === "enemy" || eb.kind === "boss")) {
      const speed = a.getLinearVelocity().length();
      if (speed > 9) {
        this.hurt(b, eb, 1, true);
        this.addShake(4);
      }
      return;
    }

    if (ea.kind === "player" && eb.kind === "pickup" && eb.perk) {
      const p = b.getPosition();
      if (eb.perk === "repair") {
        ea.hp = Math.min(this.maxHp(), ea.hp + 2);
        this.particles.score(p.x, p.y, "+2 ARMOUR");
      } else {
        this.shield = 2;
        this.shieldTimer = 14;
        this.particles.score(p.x, p.y, "SHIELD");
      }
      this.sound.play(eb.perk === "repair" ? "repair" : "shield");
      this.particles.ring(p.x, p.y, 1.6, eb.perk === "repair" ? colours.car : colours.green, 0.6);
      if (b === this.drop) {
        this.drop = null;
        this.dropRest = DROP_REST;
      }
      this.kill(b, eb, false);
      return;
    }

    if (ea.kind === "player" && eb.kind === "pickup") {
      const power = eb.power ?? "triple";
      this.powers[power] = Math.min(60, (this.powers[power] ?? 0) + this.powerSeconds());
      const stacked = Object.keys(this.powers).length >= 2;
      if (stacked) this.note((st) => void (st.stacked = true));
      this.sound.play(stacked ? "stack" : "pickup");
      const p = b.getPosition();
      this.particles.ring(p.x, p.y, 1.4, colours.acid, 0.5);
      this.particles.score(p.x, p.y, this.loadout().name.toUpperCase());
      this.kill(b, eb, false);
      return;
    }

    if (ea.kind === "player" && eb.kind === "exit" && eb.open) this.clearLevel();
  }

  /** A car goes flat under the tank, and stays there as a mark on the map. */
  private crush(body: Body, e: Entity): void {
    if (e.dead) return;
    const p = body.getPosition();
    const angle = body.getAngle();
    e.dead = true;
    this.reap.add(body);
    this.cars = this.cars.filter((c) => c !== body);
    this.sound.play("crush");
    this.sound.play("alarm", 0.5);
    this.particles.chips(p.x, p.y, e.fill, 8, 6);
    this.particles.puff(p.x, p.y, 3, "#4a4a42");
    this.addShake(3);
    this.tally(true);
    const wreck = makeBox(
      this.world,
      p.x,
      p.y,
      e.hw ?? 0.95,
      e.hh ?? 0.5,
      { ...base("wreck", e.fill, 1, 1.5), ttl: 40 },
      { static: true, sensor: true, category: CAT.DEBRIS, mask: 0, angle },
    );
    void wreck;
  }

  /** Damage, with everything that follows from it. */
  private hurt(body: Body, e: Entity, amount: number, byPlayer = true): void {
    if (e.dead) return;
    if (e.kind === "player") {
      if (this.invuln > 0) return;
      if (this.shield > 0) {
        // The shield takes it. A flash and a ring, and one fewer left.
        this.shield -= 1;
        this.invuln = 0.5;
        e.flash = 0.12;
        const p = body.getPosition();
        this.particles.ring(p.x, p.y, 2.2, colours.green, 0.4);
        this.sound.play("hit", 0.8);
        if (this.shield === 0) this.shieldTimer = 0;
        return;
      }
      this.invuln = 0.5;
      this.sound.play("hurt");
      this.addShake(9);
      this.root.dataset.hurt = "";
      window.setTimeout(() => delete this.root.dataset.hurt, 160);
    }
    e.hp -= amount;
    e.flash = 0.12;
    if (e.kind === "prop" && (e.ch === "v" || e.ch === "V") && e.hp > 0) {
      // The alarm goes off, and keeps going for a while.
      if (!(e.alarm && e.alarm > 0)) this.sound.play("alarm", 0.7);
      e.alarm = 4;
    }
    if (e.team === "enemy" && byPlayer && this.player) {
      // It knows where that came from. Turn to it, and go and look, and
      // everyone within earshot comes to see what the noise was, while the
      // whole yard sees further for a while.
      const from = this.player.getPosition();
      e.seen = { x: from.x, y: from.y };
      this.alertUntil = this.levelTime + 12;
      const here = body.getPosition();
      for (const other of this.actors) {
        const oe = entityOf(other);
        if (!oe || oe === e || oe.dead || oe.mode === "hunt" || oe.mode === "flee") continue;
        if (Vec2.distance(other.getPosition(), here) > 22) continue;
        oe.mode = "search";
        oe.seen = { x: from.x, y: from.y };
        oe.goal = oe.seen;
        oe.modeTime = 0;
        if (oe.kind === "turret") {
          const p = other.getPosition();
          oe.turret = Math.atan2(from.y - p.y, from.x - p.x);
        }
      }
      if (e.mode !== "hunt") {
        e.mode = "search";
        e.goal = e.seen;
        e.modeTime = 0;
      }
      if (e.kind === "turret") {
        const p = body.getPosition();
        e.turret = Math.atan2(from.y - p.y, from.x - p.x);
      }
    }
    if (e.hp > 0) {
      if (e.kind !== "player") this.sound.play("crack", 0.5);
      const p = body.getPosition();
      this.particles.chips(p.x, p.y, e.fill, 3, 4);
      if (e.kind === "boss") this.bossPhase(e, p.x, p.y);
      return;
    }
    this.kill(body, e, byPlayer);
  }

  private kill(body: Body, e: Entity | null, byPlayer: boolean): void {
    if (!e || e.dead) return;
    e.dead = true;
    this.reap.add(body);
    const p = body.getPosition();

    if (e.kind === "shell") return;

    if (e.kind === "pickup" || e.kind === "wreck") return;
    if (e.route) this.cars = this.cars.filter((c) => c !== body);

    if (e.kind === "barrel") {
      this.pending.push({ x: p.x, y: p.y, radius: 4.6, damage: 3, power: 13 });
      this.tally(byPlayer);
      return;
    }

    this.particles.chips(p.x, p.y, e.fill, e.kind === "tile" ? 7 : 9, 8);
    this.particles.puff(p.x, p.y, 3);

    if (e.kind === "tile" || e.kind === "prop") {
      this.debris(p.x, p.y, e);
      this.sound.play("crack");
      this.heat += 0.5;
      this.tally(byPlayer);
      return;
    }

    if (e.kind === "player") {
      this.particles.ring(p.x, p.y, 3.4, colours.car, 0.7);
      this.particles.puff(p.x, p.y, 10, "#4a4a42");
      this.addShake(16);
      this.player = null;
      this.gameOver();
      return;
    }

    if (e.kind === "factory") {
      // A works comes down loudly, and takes the yard with it a little.
      this.pending.push({ x: p.x, y: p.y, radius: 6, damage: 2, power: 12 });
      this.particles.puff(p.x, p.y, 14, "#4a4a42");
      this.addShake(14);
      this.sound.play("boom");
      this.heat += 2;
      this.tally(byPlayer, true);
      this.factories = this.factories.filter((f) => f !== body);
      if (this.originals.delete(body)) {
        this.remaining = Math.max(0, this.remaining - 1);
        if (this.remaining === 0) this.openExits();
      }
      if (!this.opts.reduced) this.hitStop = 0.2;
      return;
    }

    if (e.kind === "turret" || e.kind === "enemy" || e.kind === "boss") {
      this.pending.push({ x: p.x, y: p.y, radius: e.kind === "boss" ? 7 : 3.2, damage: 1, power: 8 });
      this.heat += 1.6;
      this.bumpCombo();
      this.tally(byPlayer, true);
      this.sound.play("enemy", e.kind === "boss" ? 1 : 0.8);
      this.actors = this.actors.filter((a) => a !== body);
      if (this.originals.delete(body)) {
        this.remaining = Math.max(0, this.remaining - 1);
        if (this.remaining === 0) this.openExits();
      } else {
        this.reinforcements = Math.max(0, this.reinforcements - 1);
      }
      if (!this.opts.reduced) this.hitStop = e.kind === "boss" ? 0.32 : 0.07;
    }
  }

  /** Chunks that fall out of something that broke, and are only in the way. */
  private debris(x: number, y: number, e: Entity): void {
    const n = e.kind === "tile" ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const body = makeBox(
        this.world,
        x + Math.cos(a) * 0.3,
        y + Math.sin(a) * 0.3,
        0.16 + Math.random() * 0.14,
        0.14 + Math.random() * 0.12,
        { ...base("debris", e.fill, 1, 1.6), ttl: 6 + Math.random() * 4 },
        {
          category: CAT.DEBRIS,
          mask: MASK.DEBRIS,
          density: 0.5,
          friction: 0.7,
          restitution: 0.25,
          linearDamping: 2.2,
          angularDamping: 3,
        },
      );
      body.applyLinearImpulse(
        new Vec2(Math.cos(a) * 1.4, Math.sin(a) * 1.4),
        body.getWorldCenter(),
        true,
      );
    }
  }

  private applyBlasts(): void {
    let guard = 0;
    while (this.pending.length && guard < 40) {
      const b = this.pending.shift();
      guard += 1;
      if (!b) break;
      this.particles.ring(b.x, b.y, b.radius, colours.acid, 0.5);
      this.particles.ring(b.x, b.y, b.radius * 0.6, colours.car, 0.36);
      this.particles.puff(b.x, b.y, 7, "#4a4a42");
      this.particles.chips(b.x, b.y, colours.yellow, 8, 11);
      this.sound.play("boom", Math.min(1, b.radius / 5));
      this.addShake(4 + b.radius * 1.8);

      const hits = blast(this.world, b.x, b.y, b.radius);
      shove(hits, b.x, b.y, b.radius, b.power);
      for (const hit of hits) {
        if (hit.entity.kind === "shell" || hit.entity.kind === "debris") continue;
        if (hit.entity.kind === "wall" || hit.entity.kind === "pickup" || hit.entity.kind === "exit") continue;
        const falloff = 1 - hit.distance / b.radius;
        const damage = Math.max(1, Math.round(b.damage * falloff));
        // Anything that drives is rattled by it: a moment of nothing, then
        // it runs from where the bang was.
        if (hit.entity.kind === "enemy" && hit.entity.style !== "factory") {
          hit.entity.stun = 0.5 + falloff * 0.5;
          hit.entity.mode = "flee";
          hit.entity.goal = { x: b.x, y: b.y };
          hit.entity.modeTime = 0;
        }
        this.hurt(hit.body, hit.entity, damage);
      }
    }
  }

  /** Bodies marked dead are destroyed here, once the world is unlocked. */
  private collect(): void {
    if (!this.reap.size) return;
    for (const body of this.reap) this.world.destroyBody(body);
    this.reap.clear();
    this.shells = this.shells.filter((s) => !entityOf(s)?.dead);
    this.actors = this.actors.filter((a) => !entityOf(a)?.dead);
  }

  // ---- pacing --------------------------------------------------------------

  private tickTimers(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.boostTimer = Math.max(0, this.boostTimer - dt);
    if (this.boostTimer <= 0) {
      const rate = (this.progress.on("turbo") ? 2.2 : 3.4) / (this.trades.includes("foot") ? 1.6 : 1);
      this.boostCharge = Math.min(1, this.boostCharge + dt / rate);
    }

    for (const name of Object.keys(this.powers) as PowerName[]) {
      const left = (this.powers[name] ?? 0) - dt;
      if (left <= 0) delete this.powers[name];
      else this.powers[name] = left;
    }

    if (this.shieldTimer > 0) {
      this.shieldTimer = Math.max(0, this.shieldTimer - dt);
      if (this.shieldTimer === 0) this.shield = 0;
    }
    this.considerDrop(dt);

    if (this.comboTimer > 0) {
      this.comboTimer = Math.max(0, this.comboTimer - dt);
      if (this.comboTimer === 0) this.combo = 0;
    }

    this.heat = Math.max(0, this.heat - dt * 0.42);
    if (this.stars() >= 5) this.note((st) => void (st.stars5 = true));
    if (this.ricochets > 0) {
      const n = this.ricochets;
      this.ricochets = 0;
      this.note((st) => void (st.bounces += n));
    }
    const player = this.player;
    const pe = player && entityOf(player);
    if (pe) pe.flash = Math.max(0, (pe.flash ?? 0) - dt);
    for (const body of bodies(this.world)) {
      const e = entityOf(body);
      if (!e) continue;
      if (e.flash) e.flash = Math.max(0, e.flash - dt);
      if (e.recoil) e.recoil = Math.max(0, e.recoil - dt * 5);
      if (e.alarm) e.alarm = Math.max(0, e.alarm - dt);
      // Anything with most of its hits gone smokes.
      if (!this.opts.reduced && e.maxHp > 1 && e.hp <= e.maxHp / 3 && (e.kind === "player" || e.kind === "enemy" || e.kind === "boss" || e.kind === "prop")) {
        if (Math.random() < dt * 4) {
          const p = body.getPosition();
          this.particles.spawn("smoke", p.x + (Math.random() - 0.5) * 0.6, p.y + (Math.random() - 0.5) * 0.6, { life: 0.9, size: 0.32, colour: "#4a4a42" });
        }
      }
      if (e.kind === "wreck") {
        e.ttl = (e.ttl ?? 0) - dt;
        if (e.ttl <= 0) {
          e.dead = true;
          this.reap.add(body);
        }
      }
      if (e.kind === "debris") {
        e.ttl = (e.ttl ?? 0) - dt;
        if (e.ttl <= 0) {
          e.dead = true;
          this.reap.add(body);
        }
      }
    }
    this.collect();

    this.spawnReinforcements(dt);
  }

  /**
   * Heat brings company. Wreck enough and the map starts sending tanks in from
   * its edges, which is a score multiplier as much as a threat, since heat
   * pays. Reinforcements never gate the exit; only the garrison does.
   */
  private spawnReinforcements(dt: number): void {
    const stars = this.stars();
    if (stars < 1 || !this.spawners.length || this.remaining === 0) return;
    this.spawnTimer -= dt;
    if (this.spawnTimer > 0 || this.reinforcements >= 6) return;
    this.spawnTimer = Math.max(4, 12 - stars * 1.8);

    const player = this.player?.getPosition();
    const spot = this.spawners
      .slice()
      .sort((a, b) => {
        if (!player) return 0;
        return (
          Vec2.distance(new Vec2(b.x, b.y), player) - Vec2.distance(new Vec2(a.x, a.y), player)
        );
      })[0];
    if (!spot) return;
    this.makeEnemy(spot.x, spot.y, stars >= 4 && Math.random() < 0.5 ? "H" : stars >= 3 && Math.random() < 0.3 ? "G" : "E", false);
    this.reinforcements += 1;
    this.particles.ring(spot.x, spot.y, 2.4, colours.car, 0.7);
    this.sound.play("hit", 0.7);
  }

  private stars(): number {
    return Math.min(5, Math.floor(this.heat / 3));
  }

  private bumpCombo(): void {
    this.combo = Math.min(5, this.combo + 1);
    this.comboTimer = 2.6;
    if (this.combo >= 5) this.note((st) => void (st.combo5 = true));
  }

  /** One more thing wrecked. Anything counts; a tank counts twice over. */
  private tally(byPlayer: boolean, tank = false): void {
    if (!byPlayer) return;
    this.score += 1;
    this.levelScore += 1;
    if (tank) {
      this.tanksWrecked += 1;
      this.levelTanks += 1;
    }
  }

  private openExits(): void {
    for (const exit of this.exits) {
      const e = entityOf(exit);
      if (e) e.open = true;
      const p = exit.getPosition();
      this.particles.ring(p.x, p.y, 3, colours.green, 0.9);
    }
    this.sound.play("open", 0.8);
  }

  // ---- ends ----------------------------------------------------------------

  private clearLevel(): void {
    if (this.opts.preview) return;
    if (this.attract) {
      // It got out. On to the next yard, and no card about it.
      this.loadLevel((this.levelIndex + 1) % levels.length);
      return;
    }
    const level = levels[this.levelIndex] as Level;
    const under = level.par - this.levelTime;
    this.sound.play("clear");
    this.setText("clearScore", String(this.levelScore));
    this.setText("clearTanks", String(this.levelTanks));
    this.setText("clearTime", `${this.levelTime.toFixed(1)}s`);
    this.setText("clearTitle", `${level.name} clear`);
    const next = this.ui.next;
    if (next) next.hidden = this.mode === "run";
    // A campaign clear has no doors. Take down whatever an endless run left
    // on the card, or they turn up here; the run branch below puts its own
    // up once it knows what they are.
    if (this.mode !== "run") this.renderDoors();
    const done = this.levelIndex + 1;
    const won = done >= levels.length;
    this.note((st) => {
      if (under > 0) st.parBeaten = true;
      st.cleared = Math.max(st.cleared, done);
      if (won) st.wins += 1;
    });

    if (this.mode === "run") {
      // A run has no end but the one you come to. Keep what is left of the
      // tank, go a yard deeper, and put the doors up.
      const pe = this.player && entityOf(this.player);
      this.carried = pe ? Math.max(1, pe.hp) : 1;
      this.depth += 1;
      this.doors = doorsFor(rng(this.runSeed + this.depth * 977), this.depth);
      this.openWorkshop(under > 0);
      this.renderDoors();
      this.setState("clear");
      return;
    }

    if (!won) this.openWorkshop(under > 0);

    if (won) {
      this.saveBest();
      this.setText("winScore", String(this.score));
      this.setText("bestWin", String(this.best));
      void this.exitFullscreen();
      this.setState("win");
      return;
    }
    this.setState("clear");
  }

  private gameOver(): void {
    if (this.attract) {
      window.setTimeout(() => {
        if (this.attract) this.loadLevel(this.levelIndex);
      }, 900);
      return;
    }
    if (this.opts.preview) {
      window.setTimeout(() => this.loadLevel(this.levelIndex), 900);
      return;
    }
    this.saveBest();
    this.setText("overTitle", this.mode === "run" ? `Run over at depth ${this.depth}` : "Scrapped");
    this.sound.play("over");
    this.setText("finalScore", String(this.score));
    this.setText("bestFinal", String(this.best));
    window.setTimeout(() => {
      if (this.state === "playing") this.setState("over");
    }, 700);
  }

  private saveBest(): void {
    if (this.score <= this.best) return;
    this.best = this.score;
    writeBest(this.best);
    this.setText("bestTitle", String(this.best));
  }

  private bossPhase(e: Entity, x: number, y: number): void {
    const want = e.hp <= e.maxHp / 3 ? 2 : e.hp <= (e.maxHp * 2) / 3 ? 1 : 0;
    if (want <= (e.phase ?? 0)) return;
    e.phase = want;
    this.addShake(12);
    this.sound.play("boom", 0.8);
    this.particles.ring(x, y, 6, colours.car, 0.8);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      this.makeProp(x + Math.cos(a) * 4.5, y + Math.sin(a) * 4.5, "B");
    }
  }

  // ---- camera and canvas ---------------------------------------------------

  private followCamera(dt: number): void {
    const body = this.player;
    if (!body) return;
    const p = body.getPosition();
    const v = body.getLinearVelocity();
    const e = entityOf(body);
    const look = e ? 2.4 : 0;
    const angle = e?.turret ?? 0;
    const wantX = p.x + v.x * 0.22 + Math.cos(angle) * look;
    const wantY = p.y + v.y * 0.22 + Math.sin(angle) * look;
    const k = 1 - Math.pow(0.0015, dt);
    this.cam.x += (wantX - this.cam.x) * k;
    this.cam.y += (wantY - this.cam.y) * k;
    // The attract loop plays behind a sheet of paper that covers the middle
    // of the screen, so it is worth standing further back: what shows round
    // the edges is then a yard being fought over rather than one green wall.
    const scale = cameraScale(this.view) * (this.attract ? 0.68 : 1);
    this.cam.scale += (scale - this.cam.scale) * (1 - Math.pow(0.02, dt));
    this.turnCamera(this.headingCam ? -Math.PI / 2 - body.getAngle() : 0, dt);
    this.clampCamera();
  }

  /** Ease the world round to `want`, the short way. */
  private turnCamera(want: number, dt: number): void {
    const k = 1 - Math.pow(0.004, dt);
    this.cam.rot += wrap(want - this.cam.rot) * k;
    if (Math.abs(this.cam.rot) < 0.0005) this.cam.rot = 0;
  }

  private driftCamera(dt: number): void {
    // The title screen looks at the map rather than at nothing.
    const t = this.time * 0.12;
    this.cam.x += (this.mapSize.w / 2 + Math.cos(t) * this.mapSize.w * 0.22 - this.cam.x) * dt * 0.7;
    this.cam.y += (this.mapSize.h / 2 + Math.sin(t * 1.3) * this.mapSize.h * 0.18 - this.cam.y) * dt * 0.7;
    this.turnCamera(0, dt);
    this.clampCamera();
  }

  /** Never show past the edge of the map, unless the world is turned, when
   *  the tank's place on the screen matters more than the paper's edge. */
  private clampCamera(): void {
    if (this.cam.rot !== 0) return;
    const halfW = this.view.width / 2 / this.cam.scale;
    const halfH = this.view.height / 2 / this.cam.scale;
    this.cam.x =
      this.mapSize.w <= halfW * 2
        ? this.mapSize.w / 2
        : clamp(this.cam.x, halfW, this.mapSize.w - halfW);
    this.cam.y =
      this.mapSize.h <= halfH * 2
        ? this.mapSize.h / 2
        : clamp(this.cam.y, halfH, this.mapSize.h - halfH);
  }

  private addShake(amount: number): void {
    if (this.opts.reduced || !this.shakeOn) return;
    this.shake = Math.min(26, this.shake + amount);
  }

  private decayShake(dt: number): void {
    this.shake = Math.max(0, this.shake - dt * 46);
    if (this.shake <= 0) {
      this.cam.shakeX = 0;
      this.cam.shakeY = 0;
      return;
    }
    this.cam.shakeX = (Math.random() - 0.5) * this.shake;
    this.cam.shakeY = (Math.random() - 0.5) * this.shake;
  }

  private draw(): void {
    const markers: Scene["markers"] = [];
    if (!this.opts.preview && (this.state === "playing" || this.state === "paused")) {
      for (const works of this.factories) {
        const e = entityOf(works);
        if (!e || e.dead) continue;
        const p = works.getPosition();
        markers.push({ x: p.x, y: p.y, colour: colours.boss });
      }
      if (this.drop) {
        const p = this.drop.getPosition();
        markers.push({ x: p.x, y: p.y, colour: colours.car });
      }
      if (this.remaining === 0) {
        for (const exit of this.exits) {
          const p = exit.getPosition();
          markers.push({ x: p.x, y: p.y, colour: colours.acid });
        }
      }
    }

    // The gunsight is drawn, so the pointer is hidden only while there is
    // somewhere to draw it; before the mouse has moved, the plain crosshair
    // is better than nothing at all.
    let pointer = this.state === "playing" && !this.opts.preview && (!this.aimLocked || LOCKED_ARC)
      ? this.input?.intent.pointer ?? null
      : null;
    // Locked, the sight sits where the gun will actually go: on the arc, at
    // the pointer's distance, so a mouse off to one side shows the limit.
    if (pointer && this.aimLocked && this.player) {
      const pos = this.player.getPosition();
      const at = screenToWorld(this.cam, this.view, pointer.x, pointer.y);
      const reach = Math.hypot(at.x - pos.x, at.y - pos.y);
      const a = this.wantAim(this.player);
      pointer = worldToScreen(this.cam, this.view, pos.x + Math.cos(a) * reach, pos.y + Math.sin(a) * reach);
    }
    if ((pointer !== null) !== this.hidCursor) {
      this.hidCursor = pointer !== null;
      if (this.hidCursor) this.root.dataset.cursor = "off";
      else delete this.root.dataset.cursor;
    }

    render(this.c, this.dpr, {
      world: this.world,
      cam: this.cam,
      particles: this.particles,
      view: this.view,
      time: this.time,
      markers,
      reduced: this.opts.reduced,
      mono: this.progress.on("ink"),
      roads: this.roads,
      aim: pointer ? { x: pointer.x, y: pointer.y, ready: this.cooldown <= 0 } : null,
    });
  }

  // ---- the heads-up display ------------------------------------------------

  private updateHud(): void {
    if (this.opts.preview) return;
    const e = this.player && entityOf(this.player);
    const hp = e ? Math.max(0, e.hp) : 0;
    const weapon = this.loadout();
    const timers = Object.values(this.powers) as number[];

    // The gauges live on the tank; the entity carries what they read.
    if (e) {
      e.boost = this.boostCharge;
      e.gun = {
        shots: weapon.shots,
        heavy: weapon.blast > 0,
        bouncy: weapon.bounces > 0,
        left: timers.length ? Math.max(0, Math.min(1, Math.min(...timers) / this.powerSeconds())) : 0,
      };
    }

    const hpEl = this.ui.hp;
    if (hpEl) {
      hpEl.setAttribute("aria-valuenow", String(hp));
      hpEl.setAttribute("aria-valuemax", String(this.maxHp()));
    }
    this.setText("weapon", weapon.name);
    this.setText("power", timers.length ? `${Math.ceil(Math.min(...timers))}s` : "∞");
    this.setText("score", String(this.score));
    this.setText("level", this.mode === "run" ? `Depth ${this.depth}` : `Level ${this.levelIndex + 1}`);
    this.setText("time", `${Math.floor(this.levelTime)}s`);
    this.setText("shield", this.shield > 0 ? `◈ ${this.shield}` : "");
    const stars = this.stars();
    this.setText("heat", stars ? "★".repeat(stars) : "☆");
    const heatEl = this.ui.heat;
    if (heatEl) heatEl.dataset.stars = String(stars);
  }

  // ---- rules that bend ------------------------------------------------------

  /**
   * What the gun is right now, from whichever power-ups are running. They
   * stack: three shells that bounce, a heavy shell that bounces and goes off
   * on every wall, three heavy shells at once. The names are for the HUD.
   */
  private loadout(): Weapon {
    const t = (this.powers.triple ?? 0) > 0;
    const b = (this.powers.bouncer ?? 0) > 0;
    const h = (this.powers.heavy ?? 0) > 0;
    const name =
      h && t && b ? "Kitchen Sink"
      : h && t ? "Broadside"
      : h && b ? "Wrecker"
      : t && b ? "Pinball"
      : h ? "Heavy"
      : t ? "Triple"
      : b ? "Bouncer"
      : "Cannon";
    const tank = this.tank();
    const st = this.stats;
    const has = (id: string) => this.trades.includes(id);
    const rate = Math.pow(0.92, st.rate) * (has("frag") ? 1.25 : 1) * (has("foot") ? 1.1 : 1);
    const punch = (1 + 0.25 * st.damage) * (has("glass") ? 2 : 1) * (has("eye") ? 0.8 : 1) * (this.progress.on("glass") ? 2 : 1);
    const fly = (1 + 0.1 * st.shell) * (has("eye") ? 1.5 : 1);
    return {
      name,
      cooldown: (h ? (t ? 1.05 : 0.95) : t ? 0.54 : b ? 0.4 : 0.42) * tank.cooldown * rate,
      speed: (h ? 26 : t ? 38 : b ? 34 : 42) * fly,
      damage: ((h ? 3 : 1) + tank.damage) * punch,
      radius: h ? 0.36 : t ? 0.17 : b ? 0.19 : 0.2,
      shots: t ? 3 : 1,
      spread: t ? 0.17 : 0,
      bounces: Math.max(b ? 3 : this.progress.on("rubber") ? 2 : 0, tank.bounces) + (has("skip") ? 1 : 0),
      blast: Math.max(h ? 3.4 : 0, has("frag") ? 1.5 : 0),
    };
  }

  private resetWorkshop(): void {
    this.stats = { armour: 0, speed: 0, damage: 0, shell: 0, rate: 0 };
    this.trades = [];
    this.points = 0;
    this.carried = 0;
    this.bonusArmour = 0;
    this.grantPower = null;
    this.grantShield = false;
  }

  private hullColour(): string {
    const paint = PAINTS.find((p) => p.id === this.paintJob.colour);
    return paint?.colour ?? this.tank().colour;
  }

  /**
   * The workshop, between levels. Points to spend depend on how the level
   * went: one for clearing it, one more for beating par, one more for
   * wrecking three tanks. One trade is offered, take it or leave it.
   */
  private openWorkshop(underPar: boolean): void {
    this.points += 1 + (underPar ? 1 : 0) + (this.levelTanks >= 5 ? 1 : 0);
    this.renderWorkshop();
  }

  /**
   * The doors out of a yard. Each says what is behind it, and one of them
   * may be an elite: a worse yard for a better reward. Choosing is the game.
   */
  private renderDoors(): void {
    const box = this.ui.doors;
    if (!box) return;
    if (this.mode !== "run") {
      box.replaceChildren();
      return;
    }
    const boss = this.depth % 4 === 0;
    box.replaceChildren(
      ...this.doors.map((door, i) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "door";
        button.dataset.door = String(i);
        if (door.elite) button.dataset.elite = "";
        const name = document.createElement("b");
        name.textContent = door.label;
        const detail = document.createElement("span");
        detail.textContent = door.detail;
        const tag = document.createElement("i");
        tag.className = "micro";
        tag.textContent = boss
          ? "Through here: the yard at the end"
          : door.elite
          ? "A bad corner, and a point for taking it"
          : `Depth ${this.depth}`;
        button.append(name, detail, tag);
        return button;
      }),
    );
  }

  /** What a door hands over on the way through. */
  private takeDoor(door: Door): void {
    switch (door.reward) {
      case "points":
        this.points += 2;
        break;
      case "trade": {
        // A trade through a door is free: the door was the price.
        const pool = TRADES.filter((t) => !this.trades.includes(t.id));
        const trade = pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
        if (trade && this.trades.length < TRADE_MAX) {
          this.trades.push(trade.id);
          // The player did not pick it, so say what it was.
          this.toast(`${trade.name}: ${trade.give}. ${trade.take}`);
        }
        break;
      }
      case "repair":
        this.carried = 0;
        break;
      case "shield":
        this.bonusArmour += 1;
        this.grantShield = true;
        break;
      case "power":
        this.grantPower = (["triple", "bouncer", "heavy"] as PowerName[])[Math.floor(Math.random() * 3)] ?? "triple";
        break;
    }
    if (door.elite) this.points += 1;
    this.nextKind = this.depth % 4 === 0 ? "boss" : door.elite ? "elite" : "yard";
  }

  private renderWorkshop(): void {
    const list = this.ui.workshopList;
    if (!list) return;
    // Points to spend are dots, one each, so the card is counted rather than
    // read. The words are kept for anything that cannot see the dots.
    const picks = this.ui.workshopPicks;
    if (picks) {
      const word = document.createElement("span");
      word.textContent = "Workshop";
      const points = document.createElement("span");
      points.className = "workshop__points";
      for (let i = 0; i < this.points; i++) points.append(document.createElement("i"));
      picks.replaceChildren(word, points);
      picks.setAttribute(
        "aria-label",
        this.points > 0 ? `Workshop: ${this.points} point${this.points === 1 ? "" : "s"} to spend` : "Workshop: spent",
      );
    }
    list.replaceChildren(
      ...STATS.map((stat) => {
        const row = document.createElement("div");
        row.className = "stat";
        const ico = document.createElement("span");
        ico.className = "stat__ico";
        ico.append(statIcon(stat.id));
        const name = document.createElement("b");
        name.className = "stat__name";
        name.textContent = stat.name;
        name.title = stat.blurb;
        const dots = document.createElement("span");
        dots.className = "stat__dots";
        for (let i = 0; i < STAT_MAX; i++) {
          const dot = document.createElement("i");
          if (i < this.stats[stat.id]) dot.dataset.on = "";
          dots.append(dot);
        }
        const up = document.createElement("button");
        up.type = "button";
        up.className = "stat__up";
        up.dataset.stat = stat.id;
        up.textContent = "+";
        up.disabled = this.points <= 0 || this.stats[stat.id] >= STAT_MAX;
        up.title = stat.blurb;
        row.append(ico, name, dots, up);
        return row;
      }),
    );
  }

  /** The paint shop: swatches, scrawls, and the tank as it will look. */
  private renderPaint(): void {
    const swatches = this.ui.paintColours;
    const decals = this.ui.paintDecals;
    const preview = this.ui.paintPreview as HTMLCanvasElement | null;
    if (swatches) {
      swatches.replaceChildren(
        ...PAINTS.map((p) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "swatch";
          button.dataset.paint = p.id;
          button.style.setProperty("--swatch", p.colour ?? this.tank().colour);
          button.setAttribute("aria-pressed", String(p.id === this.paintJob.colour));
          button.title = p.name;
          const label = document.createElement("span");
          label.className = "micro";
          label.textContent = p.name;
          button.append(label);
          return button;
        }),
      );
    }
    if (decals) {
      decals.replaceChildren(
        ...DECALS.map((d) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "decal";
          button.dataset.decal = d.id;
          button.setAttribute("aria-pressed", String(d.id === this.paintJob.decal));
          const pic = document.createElement("canvas");
          pic.className = "decal__pic";
          const label = document.createElement("span");
          label.className = "micro";
          label.textContent = d.name;
          button.append(pic, label);
          queueMicrotask(() => paintTankPreview(pic, { colour: this.hullColour(), size: 1, style: this.tankId, decal: d.id }));
          return button;
        }),
      );
    }
    if (preview) {
      paintTankPreview(preview, { colour: this.hullColour(), size: this.tank().size, style: this.tankId, decal: this.paintJob.decal, lit: true });
    }
  }

  private tank(): TankSpec {
    return tankById(this.tankId);
  }

  /** A remembered choice is only honoured if it is still earned. */
  private pickTank(id: string): TankId {
    const spec = tankById(id);
    return spec.earned(this.progress.stats) ? spec.id : "standard";
  }

  /** Five tanks in a row on the title sheet, each drawn by the game itself. */
  private renderPicker(): void {
    const strip = this.ui.tankStrip;
    if (!strip) return;
    const max = { hp: 9, drive: 1.45, gun: 1 / 0.72 };
    const cards = TANKS.map((tank) => {
      const unlocked = tank.earned(this.progress.stats);
      const on = tank.id === this.tankId;
      const card = document.createElement("button");
      card.type = "button";
      card.className = "tankcard";
      card.dataset.tankId = tank.id;
      card.dataset.state = unlocked ? (on ? "on" : "off") : "locked";
      card.setAttribute("aria-pressed", String(on));
      if (!unlocked) card.dataset.unlock = tank.unlock;
      const pic = document.createElement("canvas");
      pic.className = "tankcard__pic";
      pic.setAttribute("aria-hidden", "true");
      const name = document.createElement("b");
      name.className = "tankcard__name";
      name.textContent = tank.name;
      const tag = document.createElement("span");
      tag.className = "tankcard__tag";
      tag.textContent = unlocked ? (on ? "Driving" : "") : "Locked";
      const stats = document.createElement("span");
      stats.className = "tankcard__stats";
      for (const [id, label, fill] of [
        ["armour", "Armour", tank.hp / max.hp],
        ["speed", "Drive", tank.drive / max.drive],
        ["rate", "Gun", 1 / tank.cooldown / max.gun],
      ] as Array<[string, string, number]>) {
        // The cards are too small for words, so the icon carries the name.
        const l = statIcon(id, label);
        const bar = document.createElement("span");
        bar.className = "bar";
        bar.style.setProperty("--fill", fill.toFixed(2));
        stats.append(l, bar);
      }
      card.append(pic, name, tag, stats);
      if (!unlocked) {
        const lock = document.createElement("span");
        lock.className = "tankcard__lock";
        lock.append(statIcon("lock", "Locked"));
        card.append(lock);
      }
      return { card, pic, tank, unlocked };
    });
    strip.replaceChildren(...cards.map((c) => c.card));
    // Canvases need their layout size before they can be painted.
    for (const c of cards) {
      paintTankPreview(c.pic, {
        colour: c.tank.id === this.tankId ? this.hullColour() : c.tank.colour,
        size: c.tank.size,
        style: c.tank.id,
        decal: c.tank.id === this.tankId ? this.paintJob.decal : "none",
        locked: !c.unlocked,
      });
    }
  }

  private renderCredits(): void {
    const list = this.ui.creditsList;
    if (!list) return;
    list.replaceChildren(
      ...SOUND_CREDITS.map((credit) => {
        const li = document.createElement("li");
        const slot = document.createElement("b");
        slot.textContent = credit.slot;
        const link = document.createElement("a");
        link.href = credit.url;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = credit.title;
        const by = document.createElement("span");
        by.textContent = ` by ${credit.author} · ${credit.license}`;
        li.append(slot, link, by);
        return li;
      }),
    );
  }

  private powerSeconds(): number {
    return (this.progress.on("longfuse") ? 30 : POWER_SECONDS) * (this.trades.includes("match") ? 2 : 1);
  }

  private maxHp(): number {
    if (this.progress.on("glass")) return 1;
    const lost = (this.trades.includes("skip") ? 1 : 0) + (this.trades.includes("glass") ? 2 : 0);
    return Math.max(1, this.tank().hp + this.stats.armour + this.bonusArmour - lost);
  }

  /** Something happened worth remembering; say so if it earned a mod. */
  private note(change: Parameters<Progress["record"]>[0]): void {
    // A tank driving itself round the title screen earns nothing.
    if (this.attract) return;
    for (const mod of this.progress.record(change)) {
      this.toast(`Unlocked: ${mod.name}`);
      this.sound.play("unlock", 0.8);
    }
    for (const tank of this.progress.newTanks(TANKS)) {
      this.toast(`New in the garage: ${tank.name}`);
      this.sound.play("unlock", 0.8);
    }
    if (this.state === "achievements") this.renderAchievements();
    this.renderPicker();
  }

  private toast(text: string): void {
    const el = this.ui.toast;
    if (!el) return;
    el.textContent = text;
    el.dataset.on = "";
    window.setTimeout(() => delete el.dataset.on, 2600);
  }

  /**
   * The achievements sheet: every mod and every tank, what it takes, how far
   * along it is, and, for a mod, the switch. One list, two headings.
   */
  private renderAchievements(): void {
    const list = this.ui.achievementList;
    if (!list) return;
    const stats = this.progress.stats;
    const heading = (text: string) => {
      const h = document.createElement("p");
      h.className = "ach__group micro";
      h.textContent = text;
      return h;
    };
    const row = (opts: {
      mark: string;
      name: string;
      blurb: string;
      unlock: string;
      earned: boolean;
      progress: { value: number; target: number };
      toggle?: { id: ModId; on: boolean };
    }) => {
      const item = document.createElement("div");
      item.className = "ach__item";
      item.dataset.state = !opts.earned ? "locked" : opts.toggle?.on ? "on" : "earned";
      const mark = document.createElement("span");
      mark.className = "ach__mark";
      mark.textContent = opts.earned ? "✓" : opts.mark;
      const name = document.createElement("b");
      name.className = "ach__name";
      name.textContent = opts.name;
      const blurb = document.createElement("span");
      blurb.className = "ach__blurb";
      blurb.textContent = opts.earned ? opts.blurb : opts.unlock;
      const bar = document.createElement("span");
      bar.className = "ach__bar";
      const fill = document.createElement("span");
      fill.className = "bar";
      fill.style.setProperty("--fill", (opts.progress.value / opts.progress.target).toFixed(2));
      const count = document.createElement("span");
      count.textContent = opts.progress.target > 1
        ? `${opts.progress.value} / ${opts.progress.target}`
        : opts.earned
        ? "Done"
        : "Not yet";
      bar.append(fill, count);
      item.append(mark, name, blurb, bar);
      if (opts.toggle) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "ach__toggle";
        button.dataset.mod = opts.toggle.id;
        button.disabled = !opts.earned;
        button.setAttribute("aria-pressed", String(opts.toggle.on));
        button.textContent = opts.earned ? (opts.toggle.on ? "On" : "Off") : "Locked";
        item.append(button);
      }
      return item;
    };
    const tanks = TANKS.filter((t) => t.id !== "standard");
    list.replaceChildren(
      heading("Mods"),
      ...MODS.map((mod, i) =>
        row({
          mark: String(i + 1),
          name: mod.name,
          blurb: mod.blurb,
          unlock: mod.unlock,
          earned: mod.earned(stats),
          progress: mod.progress(stats),
          toggle: { id: mod.id, on: this.progress.on(mod.id) },
        })
      ),
      heading("Tanks"),
      ...tanks.map((tank) =>
        row({
          mark: tank.name.slice(0, 1),
          name: tank.name,
          blurb: tank.blurb,
          unlock: tank.unlock,
          earned: tank.earned(stats),
          progress: tank.progress(stats),
        })
      ),
    );
    const earned = MODS.filter((m) => m.earned(stats)).length + tanks.filter((t) => t.earned(stats)).length;
    this.setText("achievementCount", `${earned}/${MODS.length + tanks.length}`);
  }

  private syncLabels(): void {
    const check = (sel: string, on: boolean) =>
      this.root.querySelectorAll<HTMLElement>(sel).forEach((el) => el.setAttribute("aria-checked", String(on)));
    check('[data-act="aim-mouse"]', !this.aimLocked);
    check('[data-act="aim-locked"]', this.aimLocked);
    check('[data-act="shake"]', this.shakeOn);
    check('[data-act="full"]', this.full);
    this.setText("aimHint", this.aimLocked ? "Aim (locked ahead: turn the tank)" : "Aim");
    this.root.dataset.aim = this.aimLocked ? "hull" : "mouse";
  }

  /**
   * A stick or a thumb points somewhere on the screen. With the world turned
   * under it, that direction has to be turned back before the tank reads it.
   */
  private rotated(intent: Intent | null): Intent | null {
    if (!intent || this.cam.rot === 0) return intent;
    const r = -this.cam.rot;
    const cs = Math.cos(r);
    const sn = Math.sin(r);
    const spin = (v: { x: number; y: number } | null) =>
      v ? { x: v.x * cs - v.y * sn, y: v.x * sn + v.y * cs } : null;
    return { ...intent, turnTo: spin(intent.turnTo), aimDir: spin(intent.aimDir) };
  }

  // ---- the attract loop on the index card ----------------------------------

  private autoAim = 0;
  private autoTimer = 0;

  /** A tank that plays by itself, for the preview on the sandbox index. */
  private autopilot() {
    this.autoTimer += STEP;
    this.autoAim += STEP * 0.7;
    const fire = Math.sin(this.autoTimer * 2.4) > 0.86;
    return {
      throttle: 0.75,
      steer: Math.sin(this.autoTimer * 0.5) * 0.9,
      turnTo: null,
      aimDir: { x: Math.cos(this.autoAim), y: Math.sin(this.autoAim) },
      pointer: null,
      fire,
      boost: false,
    };
  }

  // ---- going away ----------------------------------------------------------

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    this.observer = null;
    this.input?.dispose();
    this.input = null;
    this.sound.dispose();
    this.offs.forEach((off) => off());
    this.offs = [];
    this.canvas.remove();
    delete this.root.dataset.state;
  }
}

// ---- small helpers ---------------------------------------------------------

const sides = new WeakMap<object, number>();
let nextSide = 0;
/** A stable number per thing, so each tank picks a side and keeps it. */
const seedOf = (o: object): number => {
  let n = sides.get(o);
  if (n === undefined) {
    n = nextSide++;
    sides.set(o, n);
  }
  return n;
};

const base = (kind: Entity["kind"], fill: string, hp: number, lw: number): Entity => ({
  kind,
  hp,
  maxHp: hp,
  fill,
  stroke: colours.ink,
  lw,
});

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** One line each, drawn as ink: a shield, chevrons, a burst, a shell, a clock. */
const ICON: Record<string, string> = {
  armour: "M12 3 20 6.5V12c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6.5Z",
  speed: "M4 6l7 6-7 6M13 6l7 6-7 6",
  damage: "M12 2.6v5.4M12 16v5.4M2.6 12H8M16 12h5.4M5.3 5.3l3.8 3.8M14.9 14.9l3.8 3.8M18.7 5.3l-3.8 3.8M9.1 14.9l-3.8 3.8",
  shell: "M12 3.4c2.6 2.2 4 5.2 4 8.6s-1.4 6.4-4 8.6c-2.6-2.2-4-5.2-4-8.6s1.4-6.4 4-8.6ZM8.3 14.2h7.4M8.6 16.4l-2.9 3.2M15.4 16.4l2.9 3.2",
  rate: "M12 8.5V12l2.8 1.9M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM9.5 1.5h5",
  lock: "M8 10.5V7.8a4 4 0 0 1 8 0v2.7M5.6 10.5h12.8a1.6 1.6 0 0 1 1.6 1.6v6.6a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 18.7v-6.6a1.6 1.6 0 0 1 1.6-1.6Z",
};

/**
 * An attribute's mark. Given a name it is a picture worth reading aloud;
 * without one it is decoration beside a word that already says it.
 */
function statIcon(id: string, label?: string): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("class", "ico");
  if (label) {
    svg.setAttribute("role", "img");
    const title = document.createElementNS(ns, "title");
    title.textContent = label;
    svg.append(title);
  } else {
    svg.setAttribute("aria-hidden", "true");
  }
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", ICON[id] ?? "");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.1");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}

/**
 * Parked: shells from opposite sides cancelling in mid-air. It works (see
 * `clashShells` and `sweptHit`, both tested), but shooting incoming fire out
 * of the air made the game too easy. Ideas for bringing it back: only heavy
 * shells, only on a power-up or a trade, or a small chance per meeting.
 */
const SHELLS_CANCEL = false;

/**
 * Parked: locked aim with a short mouse-steered arc either side of the nose.
 * It felt wrong once the pointer left the screen or sat outside the arc: the
 * gun pinned to one edge and the sight no longer matched the mouse. Locked
 * aim is plain nose-only until that is solved (an arc steered by mouse
 * movement rather than position might be the fix).
 */
const LOCKED_ARC = false;

/** How far that arc would let the gun swing either side of the nose: 30°. */
const AIM_ARC = 0.52;

/** Up the page. Screen y runs down, so up is a quarter turn back. */
const NORTH = -Math.PI / 2;

/** An angle folded back into -π…π, so a turn always takes the short way. */
const wrap = (a: number) => {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
};

const offset = (p: Vec2, angle: number, distance: number) =>
  new Vec2(p.x + Math.cos(angle) * distance, p.y + Math.sin(angle) * distance);

function readPaint(): PaintJob {
  try {
    const raw = JSON.parse(localStorage.getItem(`paper-tank.${PAINT_KEY}`) ?? "{}") as Partial<PaintJob>;
    return { colour: typeof raw.colour === "string" ? raw.colour : "tank", decal: typeof raw.decal === "string" ? raw.decal : "none" };
  } catch {
    return { colour: "tank", decal: "none" };
  }
}

function readBest(): number {
  try {
    return Number(localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeBest(value: number): void {
  try {
    localStorage.setItem(BEST_KEY, String(value));
  } catch {
    // No store; the best score lasts as long as the visit.
  }
}
